<template>
  <div class="platform-access-page">
    <div class="page-header">
      <div class="page-header__copy">
        <p class="eyebrow">PREIKA PROVIDER CONTROL</p>
        <h1>契約企業一覧</h1>
        <p>
          PREIKA運営管理者が、契約企業の開通、契約プラン、利用人数上限、利用可能機能、
          最初の契約企業管理者への招待を管理します。契約企業側の権限管理とは別の運営画面です。
        </p>
      </div>
      <!--
        **見るだけの人には、押せる形で出さない**（2026-09-23）。
        中の「作成して招待メールを送信」は押せない形にしてあるが、
        入口が押せると、開いて入れてから断られる。
      -->
      <CButton
        v-if="canCreateAccount"
        class="page-header__action"
        color="primary"
        @click="openCreate"
      >
        契約企業を追加
      </CButton>
      <p v-else class="page-header__action text-muted">
        契約企業の追加には「契約企業を作成」の権限が必要です。
      </p>
    </div>

    <CAlert color="info" class="provider-scope-alert">
      この画面はPREIKA運営用です。契約企業管理者は自社内では最上位ですが、
      他社・契約プラン・PREIKA運営権限を変更できません。
    </CAlert>
    <!--
      画面上端の帯。**page-banner は検査の目印。**画面の中には説明用の
      CAlert がいくつもあるので、色だけで拾うとそちらまで数えてしまう。

      **ここに出すのは、読み込みの失敗と、上端の［契約企業を追加］の結果だけ**
      （2026-09-26）。会社を選んだあとのタブの操作（契約の更新・利用可能機能・
      招待・一時パス）は200行以上下にあり、ここに出すと押した所から見えなかった。
      それらの結果は、押したボタンのそば・押した行・開いている小窓の中に出す。
    -->
    <CAlert v-if="successMessage" class="page-banner" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <!--
      **通ったが届かなかった、を緑で出さない。**
      招待は作成できてもメール送信だけ落ちることがある。緑で「送信しました」と
      出すと、運営はそこで手を離す。招待された人には何も届かない。
    -->
    <CAlert v-if="warningMessage" class="page-banner" color="warning" close-button @update:show="warningMessage = ''">
      {{ warningMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" class="page-banner" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <CCard class="access-card">
      <CCardHeader class="section-header">
        <strong>契約企業</strong>
        <CButton
          class="section-header__action"
          size="sm"
          color="secondary"
          variant="outline"
          :disabled="loading"
          @click="reloadAccounts"
        >
          再読込
        </CButton>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="text-center py-5"><CSpinner color="primary"/></div>
        <template v-else>
          <!-- lang="ja"：会社名などの長い名前を、文節で折る（「SF3確かめ／株式会社」）ため（account-list-shell の word-break）。 -->
          <div class="desktop-only responsive-table-shell account-list-shell" lang="ja">
            <CDataTable
              :items="normalizedAccounts"
              :fields="accountFields"
              hover
              striped
              bordered
              pagination
              :items-per-page="10"
              no-items-view="契約企業がありません。"
            >
              <template #actions="{item}">
                <td class="py-2 action-cell">
                  <CButton size="sm" color="info" variant="outline" @click="selectAccount(item)">
                    管理
                  </CButton>
                </td>
              </template>
              <!-- 契約期間は「日付 〜」と「（期限なし）」などのまとまりの間でだけ折る（つなげると元の字と同じ）。 -->
              <template #contractPeriodLabel="{item}">
                <td>
                  <span v-for="(phrase, i) in periodPhrases(item.contractPeriodLabel)" :key="i" class="period-phrase">{{ phrase }}</span>
                </td>
              </template>
            </CDataTable>
          </div>

          <div class="mobile-only mobile-record-list" aria-label="契約企業一覧">
            <article v-for="account in normalizedAccounts" :key="account.id" class="mobile-record-card">
              <div class="mobile-record-card__header">
                <strong>{{ account.name }}</strong>
                <CBadge color="secondary">{{ account.contractStatusLabel }}</CBadge>
              </div>
              <dl class="mobile-detail-list">
                <div><dt>契約企業</dt><dd>{{ account.contractorName }}</dd></div>
                <div><dt>契約期間</dt><dd>{{ account.contractPeriodLabel }}</dd></div>
                <div><dt>販売代理店</dt><dd>{{ account.resellerName }}</dd></div>
                <div><dt>同じ契約の会社数</dt><dd>{{ account.tenantCountLabel }}</dd></div>
                <div><dt>契約プラン</dt><dd>{{ account.planName }}</dd></div>
                <div><dt>利用人数上限</dt><dd>{{ account.userLimitLabel }}</dd></div>
              </dl>
              <CButton class="mobile-primary-action" color="info" variant="outline" @click="selectAccount(account)">
                この企業を管理
              </CButton>
            </article>
            <div v-if="!normalizedAccounts.length" class="mobile-empty-state">
              契約企業がありません。
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!--
      **プランマスター。**契約で選べるプランの一覧。

      ここに置いたのは、**プランを選ぶ人と、プランを作る人が同じ**
      だから。プランを増やす口は製品に1つも無く、いままで migration か
      本番SQLでしか作れなかった。

      料金を変える操作だけは「請求管理」に置いたままにしてある。
      **請求額が0円になったとき、その場で原因が分かるようにするため。**
    -->
    <CCard class="access-card plan-master-card">
      <CCardHeader class="plan-master-header">
        <div>
          <strong>プランマスター</strong>
          <small>
            契約で選べるプランの一覧です。料金の変更は「請求管理」画面から行ってください。
          </small>
        </div>
        <CButton
          v-if="canEditPlans"
          color="primary"
          size="sm"
          @click="openAddPlan"
        >プランを追加</CButton>
      </CCardHeader>
      <CCardBody>
        <CDataTable
          :items="planMasterRows"
          :fields="planFields"
          hover
          striped
        >
          <template #actions="{ item }">
            <td>
              <CButton
                v-if="canEditPlans"
                color="secondary"
                size="sm"
                @click="openRenamePlan(item)"
              >名称・表示順を変更</CButton>
            </td>
          </template>
        </CDataTable>
        <p v-if="!planMasterRows.length" class="text-muted plan-master-note">
          プランがありません。
        </p>
        <!--
          **読めていないのに0円と書かない。**料金の口は請求の列が
          入っていない環境では落ちる。金額なしで表を出したことを、
          その場で伝える。
        -->
        <p v-if="!planPricingLoaded" class="text-muted plan-master-note">
          料金データを読み込めませんでした。金額を非表示にしています。
        </p>
        <p v-else class="text-muted plan-master-note">
          料金は月額（税抜）です。金額の変更は、「PREIKA運営」の
          「請求管理」＞「プランの料金」から行ってください。
          <b>請求には、対象月の初日に有効だった料金を使います。</b>
          過去の月をあとから確定しても、その月に有効だった料金で計算されます。
        </p>
        <p v-if="!canEditPlans" class="text-muted plan-master-note">
          プランの追加・変更には「契約プラン変更」権限が必要です。
        </p>
      </CCardBody>
    </CCard>

    <CCard
      v-if="selectedAccount"
      ref="selectedAccountCard"
      class="access-card selected-account-card"
    >
      <CCardHeader class="selected-account-header">
        <div>
          <strong>{{ selectedAccount.name }} の契約管理</strong>
          <small>契約企業側の利用者・権限グループの設定は、権限管理で行います。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <CTabs class="access-tabs" @update:activeTab="onManagementTabChanged">
          <!--
            **一時パス（場面 S-95）。**

            出せるのは PREIKA運営だけ。顧客企業の管理者の画面には
            置かない——置くと、会社側の管理者が自分の第2要素を
            迂回する資格情報を自分で作れる。

            **入口が開いているときだけタブを出す。**閉じているのに
            出すと、押しても必ず断られる。「機能はOFF」なのに
            壊れた機能に見える。
          -->
          <CTab v-if="temporaryPassEnabled" title="一時パス">
            <div class="pt-3">
              <p class="text-muted">
                二段階認証を両方失った方へ、電話で読み上げて渡す
                一時パスを出します。<b>この会社の管理者には発行できません。</b>
              </p>
              <!--
                **数字を打たせない（場面 S-95）。**

                一時パスは<b>その人になりすませる文字列</b>。対象を内部の番号の
                転記で決めると、桁を1つ打ち間違えただけで<b>同じ会社の別の人へ
                正しく発行される。</b>会社の境目はサーバが守っているので、
                そこでは止まらない。

                **番号自体が間違っているだけ**なので、サーバには検知しようが
                ない。人が取り違えないようにするのは、ここの仕事。
              -->
              <CSelect
                class="platform-issue-target"
                label="対象の利用者"
                :value="issueUserId === null ? '' : String(issueUserId)"
                :options="temporaryPassUserOptions"
                placeholder="選択してください"
                description="氏名およびメールアドレスを確認の上、選択してください。"
                @update:value="selectTemporaryPassUser"
              />
              <p v-if="!normalizedTemporaryPassUsers.length" class="text-muted">
                この会社の利用者を読み込めませんでした。
                <b>読み込めないあいだは出せません。</b>
              </p>
              <CButton
                class="platform-issue-pass"
                color="primary"
                variant="outline"
                :disabled="!issueUserId"
                @click="startIssueTemporaryPass()"
              >一時パスを出す</CButton>
              <ActionResultMessage :text="issueStartError" type="danger"/>
            </div>
          </CTab>

          <CTab title="契約" active>
            <CRow class="pt-3 contract-form-row">
              <CCol sm="12" md="4">
                <!-- **v-model は CSelect では死ぬ。**選んでも送られない。 -->
                <CSelect
                  label="契約プラン"
                  :options="planOptions"
                  :value="contractForm.planId"
                  @update:value="value => { contractForm.planId = selectedValue(value) }"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CSelect
                  label="契約状態"
                  :options="statusOptions"
                  :value="contractForm.contractStatus"
                  @update:value="value => { contractForm.contractStatus = selectedValue(value) }"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CInput
                  label="利用人数上限"
                  type="number"
                  min="1"
                  v-model.number="contractForm.userLimit"
                  description="未入力の場合は無制限となります。"
                />
              </CCol>
            </CRow>
            <!--
              **期間は機能の門番。**外れるとその会社の機能が全部落ちる。
              いままで画面に1文字も出ておらず、入れる口も無かった
              （入れられないのに、入っていたら止まる状態だった）。
            -->
            <CRow class="contract-form-row">
              <CCol sm="12" md="4">
                <CInput
                  label="契約開始日"
                  type="date"
                  v-model="contractForm.contractStartDate"
                  description="未入力の場合は開始日制限なしとなります。"
                />
              </CCol>
              <CCol sm="12" md="4">
                <CInput
                  label="契約終了日"
                  type="date"
                  v-model="contractForm.contractEndDate"
                  :description="endDateWarning"
                />
              </CCol>
            </CRow>
            <CButton class="primary-action" color="primary" :disabled="saving" @click="saveContract">
              契約を更新
            </CButton>
            <ActionResultMessage v-bind="contractResult"/>
          </CTab>

          <!--
            お支払い方法（2026-10-11 に見るだけで足し、2026-10-15 に許可の保存を移した）。
            **「契約」の隣に置く。**

            この会社に許可するお支払い方法（請求書払い・クレジットカード）の入・切と
            ［保存する］、お客様のいまのお支払い方法・登録済みのカード（種類・下4桁・有効期限）。
            **請求書払いの許可の入・切は、前は「利用可能機能」タブにあった。**押せる人は
            「利用可能機能」のスイッチと同じ（canEditFeatures。back も同じ権限を要求する）。
            カードの登録・変更・削除は、運営の画面からはできない。

            **このタブを開くたびに読み直す**（onManagementTabChanged）。お客様がカードを
            登録したあとに開いたとき、前の値を見せない。
            下の枠（paymentMethodsTabBody）が表に出たかどうかで、開いたことを見分ける。
          -->
          <CTab title="お支払い方法">
            <div ref="paymentMethodsTabBody">
              <AccountPaymentMethods
                :account-id="selectedAccount.id"
                :reload-key="accountRequestSeq + ':' + paymentMethodsRevision"
                :can-edit="canEditFeatures"
              />
            </div>
          </CTab>

          <!--
            契約に関わる会社。**「契約」タブの隣に置く。**
            契約企業・請求先・販売代理店・代行者は契約の一部で、
            テナントが扱う取引先（クライアント）とは別のもの。
          -->
          <CTab title="契約に関わる会社">
            <ContractParties :account-id="selectedAccount.id" :can-edit="canEditContract"/>
          </CTab>

          <!--
            初期特別価格（0100）。**請求額が変わるので、契約の隣に置く。**
            「最初の N か月は 基本料 ○円・1人あたり ○円」。0円なら無料。
          -->
          <CTab title="初期特別価格">
            <ContractIntroPrice
              :account-id="selectedAccount.id"
              :can-edit="canEditContract"
              @saved="contractRevision += 1"
            />
          </CTab>

          <!--
            **料金の上書きは1か所にまとめる**（別の PC の設計 §5「契約ごとの帯は最低月額と
            同じタブの中。別タブにしない」、2026-09-27）。タブは「料金・割引」（T1）の1つで、
            中に最低月額（0107）と人数割（0108）を**計算の順**に並べる（割引のあとに最低月額が効く
            ので、画面では最低月額を上、人数割を下にして、上から読んだときに「何が請求額を
            決めているか」が1つの場所で分かるようにする）。
            見出しはそれぞれの部品のカードの頭（「最低月額」T2・「人数割」T3）。ここに別の
            見出しを重ねない（同じ語が2段になる）。**初期特別価格の隣に置く**（請求額が変わる）。

            **reload-key は、会社を選び直すたびに増える番号。**同じ会社の［管理］を
            もう一度押したときも読み直す（会社の番号だけを見ていると、同じ会社では
            読み直さず、他の人が変えた値や、保存し損ねた入力が残る）。
            **同じ会社の［管理］の中で「契約」タブや「初期特別価格」を保存したときも
            読み直す**（contractRevision）。プランを替えると選択肢1の額・要約が、初期特別価格を
            変えると「今月は当てない月か」の色が変わるのに、タブは中身を作ったまま持つ。
            **2つの部品は別々に保存する**（［保存する］はそれぞれのカードの中。片方の保存で
            もう片方の入力を送らない）。
          -->
          <CTab title="料金・割引">
            <div class="contract-pricing-overrides">
              <ContractMinimumCharge
                :account-id="selectedAccount.id"
                :reload-key="accountRequestSeq + ':' + contractRevision"
                :can-edit="canEditContract"
              />
              <ContractVolumeDiscount
                :account-id="selectedAccount.id"
                :reload-key="accountRequestSeq + ':' + contractRevision"
                :can-edit="canEditContract"
              />
            </div>
          </CTab>

          <!--
            請求と入金。**「契約」の近くに置く。**

            契約企業を見ていて「この会社、払ってる？」と思っても、
            そこから支払い状況へ辿る道がどこにも無かった。請求管理を
            開いて、月を切り替えて、会社を目で探すしかなかった
            （2026-09-18、利用者の指摘）。

            **読むだけ。**入金の記録も請求書の発行も請求管理で行う。
            同じことを2か所からできるようにすると、どちらで操作したか
            分からなくなる。
          -->
          <CTab v-if="canSeeBilling" title="請求・入金">
            <AccountBillingHistory
              :account-id="selectedAccount.id"
              :account-name="selectedAccount.name"
            />
          </CTab>

          <!--
            **お支払い方法の許可（請求書払い・クレジットカード・口座振替）の行は出さない**
            （2026-10-15）。入・切は「お支払い方法」タブへ移した。2つの場所に同じ入・切を
            置かず、ここには行き先の案内を1行だけ残す。back の一覧の答え（GET /features）は
            変えていない（back を先に配備しても、前の画面で請求書払いを入・切できるように）。
          -->
          <CTab title="利用可能機能">
            <div class="pt-3">
              <CAlert color="secondary">
                ここで契約企業全体の利用可能機能を設定します。各スタッフが実際に利用できる操作は、
                契約企業側の権限グループと権限でも絞り込まれます。
              </CAlert>
              <p class="feature-payment-moved">{{ paymentTexts.featuresMoved }}</p>
              <p v-if="!canEditFeatures" class="text-muted">
                閲覧のみです。利用可能機能の変更は、利用可能機能を管理できる運営の担当者が行います。
              </p>
              <div class="desktop-only responsive-table-shell">
                <CDataTable :items="featuresOnThisTab" :fields="featureFields" bordered striped>
                  <template #enabled="{item}">
                    <td class="switch-cell">
                      <CSwitch
                        :key="item.featureCode + '-' + featureRevision"
                        color="success"
                        :checked="item.enabled"
                        :disabled="!canEditFeatures"
                        @update:checked="value => changeFeature(item, value)"
                      />
                      <!-- 切り替えた結果は、そのスイッチの下に出す。 -->
                      <ActionResultMessage v-bind="featureResultOf(item.featureCode)"/>
                    </td>
                  </template>
                </CDataTable>
              </div>

              <div class="mobile-only mobile-record-list" aria-label="利用可能機能一覧">
                <article v-for="feature in featuresOnThisTab" :key="feature.featureCode" class="mobile-record-card mobile-feature-card">
                  <div class="mobile-record-card__header">
                    <strong>{{ feature.featureName }}</strong>
                    <CBadge color="secondary">{{ feature.sourceLabel }}</CBadge>
                  </div>
                  <div class="mobile-switch-row">
                    <span>{{ feature.enabled ? '利用可' : '利用不可' }}</span>
                    <CSwitch
                      :key="feature.featureCode + '-' + featureRevision"
                      color="success"
                      :checked="feature.enabled"
                      :disabled="!canEditFeatures"
                      @update:checked="value => changeFeature(feature, value)"
                    />
                  </div>
                  <ActionResultMessage v-bind="featureResultOf(feature.featureCode)"/>
                </article>
                <div v-if="!featuresOnThisTab.length" class="mobile-empty-state">
                  利用可能機能がありません。
                </div>
              </div>
            </div>
          </CTab>

          <CTab title="最初の管理者招待">
            <div class="pt-3">
              <div v-if="invitationLoading" class="text-center py-5"><CSpinner color="primary"/></div>
              <template v-else>
                <CAlert color="info">
                  最初の契約企業管理者は、メールの期限付きリンクから本人がパスワードを設定して受諾します。
                  AKI側で初期パスワードを作成・共有しません。
                </CAlert>

                <!--
                  招待の中身を出せない契約企業。**エラーではない。**
                  ここで赤い帯を出すと、運営の担当者は壊れたと思って
                  手が止まる。再送する相手が決まっていないので、操作は
                  出さない。

                  **出せない理由は2つあり、担当者にとっては別の話。**
                    NONE                     招待した記録が1件も無い
                    INVITED_USER_UNAVAILABLE 記録はあるが、招待した相手を
                                             もう引けない（退職などで無効化）
                  同じ文言にすると、後者が「一度も招待していない会社」に
                  見える。見出しもバッジも本文も分ける。
                -->
                <section
                  v-if="ownerInvitationHasNothingToShow"
                  class="invitation-card invitation-card--none"
                >
                  <div class="invitation-card__header">
                    <div>
                      <p>契約企業管理者（自社最上位）</p>
                      <h3>{{ ownerInvitationNoticeHeading }}</h3>
                    </div>
                    <CBadge color="secondary">{{ ownerInvitationNoticeBadge }}</CBadge>
                  </div>
                  <p class="invitation-message">
                    {{ ownerInvitation.message || ownerInvitationNoticeFallback }}
                  </p>
                </section>

                <section v-else-if="ownerInvitation" class="invitation-card">
                  <div class="invitation-card__header">
                    <div>
                      <p>契約企業管理者（自社最上位）</p>
                      <h3>{{ ownerInvitation.invitedName || '氏名未設定' }}</h3>
                    </div>
                    <CBadge :color="invitationStatusColor(shownInvitationStatus(ownerInvitation))">
                      {{ invitationStatusLabel(shownInvitationStatus(ownerInvitation), ownerInvitation.emailSent, ownerInvitation.emailDelivery) }}
                    </CBadge>
                  </div>

                  <dl class="invitation-detail-list">
                    <div><dt>メールアドレス</dt><dd>{{ ownerInvitation.maskedEmail || '確認できません' }}</dd></div>
                    <div><dt>受諾期限</dt><dd>{{ formatDateTime(ownerInvitation.expiresAt) }}</dd></div>
                    <div><dt>最終送信日時</dt><dd>{{ formatDateTime(ownerInvitation.sentAt) }}</dd></div>
                    <div><dt>送信回数</dt><dd>{{ ownerInvitation.sentCount == null ? '0' : ownerInvitation.sentCount }}回</dd></div>
                  </dl>

                  <p v-if="ownerInvitation.message" class="invitation-message">{{ ownerInvitation.message }}</p>

                  <div class="invitation-actions">
                    <CButton
                      color="primary"
                      variant="outline"
                      :disabled="saving || !canResendInvitation"
                      @click="resendInvitation"
                    >
                      招待メールを再送
                    </CButton>
                    <CButton
                      color="info"
                      variant="outline"
                      :disabled="saving || !canChangeInvitation"
                      @click="openReplaceInvitation"
                    >
                      招待先を変更
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      :disabled="saving || !canChangeInvitation"
                      @click="revokeInvitation"
                    >
                      招待を取消
                    </CButton>
                  </div>
                </section>

                <div v-else class="mobile-empty-state">
                  招待情報を取得できませんでした。画面を再読み込みしてください。
                </div>
                <!--
                  ［再送］［招待先を変更］［取消］の結果。招待のカードのすぐ下に出す。
                  カードの中に置くと、取消などで招待の状態が変わってカードが
                  入れ替わったときに、結果ごと消える。
                -->
                <ActionResultMessage v-bind="invitationResult"/>
              </template>
            </div>
          </CTab>

          <!--
            **このタブを開くたびに、履歴を読み直す**（onManagementTabChanged。2026-10-09）。
            同じ［管理］の中で保存してからここを押すと、開いたときの一覧のままで、いまの
            保存の行が出ていなかった。下の枠（auditLogTabBody）が表に出たかどうかで、
            このタブを開いたことを見分ける。
          -->
          <CTab title="操作履歴">
            <div ref="auditLogTabBody" class="pt-3">
              <div class="desktop-only responsive-table-shell">
                <CDataTable
                  :items="normalizedAuditLogs"
                  :fields="auditFields"
                  bordered
                  striped
                  pagination
                  :items-per-page="10"
                />
              </div>

              <div class="mobile-only mobile-record-list" aria-label="操作履歴一覧">
                <article v-for="(log, index) in normalizedAuditLogs" :key="`${log.createdAt}-${index}`" class="mobile-record-card">
                  <div class="mobile-record-card__header">
                    <strong>{{ log.actionLabel }}</strong>
                    <span class="mobile-record-card__date">{{ log.createdAt || '日時未記録' }}</span>
                  </div>
                  <dl class="mobile-detail-list">
                    <div><dt>操作者</dt><dd>{{ log.actorName }}</dd></div>
                    <div><dt>対象</dt><dd>{{ log.targetLabel }}</dd></div>
                    <div><dt>変更内容</dt><dd>{{ log.changeSummary }}</dd></div>
                  </dl>
                </article>
                <div v-if="!normalizedAuditLogs.length" class="mobile-empty-state">
                  操作履歴がありません。
                </div>
              </div>
            </div>
          </CTab>
        </CTabs>
      </CCardBody>
    </CCard>

    <CModal
      title="契約企業の新規作成と管理者招待"
      :show="createModal"
      @update:show="closeCreate"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <CAlert color="info">
        企業・契約を作成した後、最初の契約企業管理者へ期限付き招待メールを送信します。
        パスワードは本人が招待受諾画面で設定します。
      </CAlert>

      <h6 class="form-block-title">会社・契約</h6>
      <!--
        **必須の項目には印を付ける。**印が無いと、任意の電話番号や
        契約プランと見た目が同じになる。全部入れたつもりの人には、
        押せないボタンだけが残る。
      -->
      <CRow>
        <CCol sm="12" md="6">
          <label>会社名<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.companyName"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>会社メール<span class="asterisk">*</span></label>
          <CInput type="email" v-model.trim="createForm.companyEmail"/>
        </CCol>
        <CCol sm="12" md="6"><CInput label="電話番号" v-model.trim="createForm.telephone"/></CCol>
        <CCol sm="12" md="6">
          <CSelect
            label="契約プラン"
            :options="planCodeOptions"
            :value="createForm.planCode"
            @update:value="value => { createForm.planCode = selectedValue(value) }"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CSelect
            label="契約状態"
            :options="newStatusOptions"
            :value="createForm.contractStatus"
            @update:value="value => { createForm.contractStatus = selectedValue(value) }"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="利用人数上限"
            type="number"
            min="1"
            v-model.number="createForm.userLimit"
            description="未入力の場合は無制限となります。"
          />
        </CCol>
      </CRow>

      <h6 class="form-block-title">連絡先（権限グループとは別管理）</h6>
      <!--
        **空のまま作らせない。**契約タブに連絡先の入力欄は無く、契約の
        更新はプラン・契約状態・利用人数上限しか受け取らない。つまり
        **ここで入れそこねると、あとから画面では直せない。**

        とはいえ3つとも必須にすると、同じアドレスを3回打たせることに
        なる。未入力なら会社メールで埋めて、**届く先を必ず1つ残す。**
      -->
      <CRow>
        <CCol sm="12" md="6">
          <CInput
            label="契約連絡先メール"
            type="email"
            v-model.trim="createForm.contractContactEmail"
            description="未入力の場合は会社のメールアドレスを使用します。"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="請求連絡先メール"
            type="email"
            v-model.trim="createForm.billingContactEmail"
            description="未入力の場合は会社のメールアドレスを使用します。"
          />
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="セキュリティ連絡先メール"
            type="email"
            v-model.trim="createForm.securityContactEmail"
            description="未入力の場合は会社のメールアドレスを使用します。"
          />
        </CCol>
      </CRow>

      <h6 class="form-block-title">最初の契約企業管理者</h6>
      <CRow>
        <CCol sm="12" md="6">
          <label>氏名<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.ownerName"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>メールアドレス<span class="asterisk">*</span></label>
          <CInput type="email" v-model.trim="createForm.ownerEmail"/>
        </CCol>
        <CCol sm="12" md="6">
          <label>社員番号<span class="asterisk">*</span></label>
          <CInput v-model.trim="createForm.ownerEmployeeId"/>
        </CCol>
      </CRow>
      <p class="form-note">
        招待受諾後、この方は自社利用者・権限グループ・権限・会社設定を管理できます。
        PREIKA全体の運営権限は付与されません。
      </p>

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**

        errorMessage は画面のいちばん上に出る。モーダルが開いている
        あいだ、そこは幕の裏で読めない。作成できない理由をそこへ書いても
        **運営には届かない。**
      -->
      <CAlert v-if="missingFields.length" color="warning" class="mb-3">
        <b>あと{{ missingFields.length }}項目入力すると、作成できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in missingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <!--
        **断られた理由も、同じ幕の中に出す。**押せなかった理由だけを
        幕の中に出しても、押せたあと断られた理由が画面上端では、
        <b>押しても画面が1ドットも変わらない</b>ままになる。運営には
        押せていないのか断られたのかが分からない。
      -->
      <CAlert v-if="createError" color="danger" class="mb-3">
        {{ createError }}
      </CAlert>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeCreate">キャンセル</CButton>
          <CButton
            class="create-submit"
            color="primary"
            :disabled="saving || missingFields.length > 0"
            @click="createAccount"
          >
            {{ saving ? '作成中...' : '作成して招待メールを送信' }}
          </CButton>
        </div>
      </template>
    </CModal>

    <CModal
      title="招待先を変更"
      :show="replaceInvitationModal"
      @update:show="closeReplaceInvitation"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <CAlert color="warning">
        現在の招待リンクは無効になります。新しい担当者へ新しい期限付き招待メールを送信します。
      </CAlert>
      <label>氏名<span class="asterisk">*</span></label>
      <CInput v-model.trim="replaceInvitationForm.ownerName"/>
      <label>メールアドレス<span class="asterisk">*</span></label>
      <CInput type="email" v-model.trim="replaceInvitationForm.ownerEmail"/>
      <!--
        **社員番号はここでは必須にしない。**利用者の登録・更新では任意
        （employee|max:20）で、受諾後に利用者の更新から入れられる。
        ここだけ必須にすると、まだ番号の決まっていない担当者へ招待を
        出し直せない。
      -->
      <CInput
        label="社員番号"
        v-model.trim="replaceInvitationForm.ownerEmployeeId"
        description="任意です。受諾後に利用者の更新からも入れられます。"
      />

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**
        前は空のまま押すと早期returnで戻るだけで、画面が1ドットも
        変わらなかった。
      -->
      <CAlert v-if="replaceMissingFields.length" color="warning" class="mb-3">
        <b>あと{{ replaceMissingFields.length }}項目入力すると、変更できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in replaceMissingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <!--
        **断られた理由も、同じ幕の中に出す。**ここは<b>いまの招待リンクを
        捨てる</b>操作。通ったのか断られたのかが分からないまま閉じられると、
        新しい担当者にも古い担当者にも届いていない状態を疑えない。
      -->
      <CAlert v-if="replaceError" color="danger" class="mb-3">
        {{ replaceError }}
      </CAlert>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeReplaceInvitation">キャンセル</CButton>
          <CButton
            class="replace-invitation-submit"
            color="primary"
            :disabled="saving || replaceMissingFields.length > 0"
            @click="replaceInvitation"
          >
            変更して招待を再送
          </CButton>
        </div>
      </template>
    </CModal>
    <!-- 出す前に確かめる（場面 S-95）。取り返しがつかない。 -->
    <CModal
      :show="Boolean(issueTarget) && !issuedPass"
      title="一時パスを出します"
      color="primary"
      @update:show="cancelIssueTemporaryPass"
    >
      <!--
        **IDだけを最終確認に使わない。**番号は人が照合できない。
        氏名とメールアドレス、そしてどの会社かまで出して、
        <b>電話の相手と突き合わせられる形</b>にする。
      -->
      <div v-if="issueTargetUser" class="platform-issue-confirm">
        <p class="platform-issue-confirm__name">
          <b>{{ issueTargetUser.name }}</b>
        </p>
        <p class="platform-issue-confirm__email">
          {{ issueTargetUser.email }}
        </p>
        <p class="platform-issue-confirm__company">
          {{ selectedAccount ? selectedAccount.name : '' }}
        </p>
        <p>に一時パスを発行します。</p>
        <p class="text-muted">利用者ID {{ issueTarget }}</p>
      </div>

      <!--
        **警告であって、止める材料ではない。**社内に救える別の人が
        居ても出せる。止めると、その人が休みだったり連絡がつかなかったり
        する場面で誰も救えなくなる。居るかどうかはサーバが数える。
      -->
      <CAlert v-if="alternativeRecoveryAvailable" color="warning">
        社内管理者による通常の復旧経路が利用できる可能性があります。
        それでも一時パスを発行する場合は、理由を入力してください。
      </CAlert>

      <CAlert v-if="livePassId" color="info">
        この方には、まだ使える一時パスがあります。
        新しく出すと、<b>いまの1本は使えなくなります。</b>
      </CAlert>

      <label>理由（記録に残ります）<span class="asterisk">*</span></label>
      <CInput
        v-model="issueReason"
        placeholder="例: 端末を紛失、ほかに外せる管理者が居ない"
        maxlength="200"
      />
      <!--
        **自由入力の数字にしない。**使える時間はサーバが範囲を決めて
        いるのに、画面側は何分でも打てた。範囲外を打った運営には
        「出せませんでした」しか返らない——電話口で本人を待たせたまま、
        直し方の分からない行き止まりになる。**打てない形にする。**
      -->
      <CSelect
        class="platform-issue-lifetime"
        label="使える時間"
        :value="String(issueLifetimeMinutes)"
        :options="lifetimeMinuteOptions"
        @update:value="selectLifetimeMinutes"
      />

      <!--
        **押せない理由は、押せないボタンと同じ幕の中に出す。**
        理由は必須だが、空のときに出ていたのは無効なボタンだけだった。
      -->
      <CAlert v-if="issueMissingFields.length" color="warning" class="mb-3">
        <b>あと{{ issueMissingFields.length }}項目入力すると、発行できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in issueMissingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>

      <!--
        **断られた理由も、この幕の中に出す**（2026-09-26）。前は幕を閉じて
        画面上端へ出していたが、上端はタブから200行以上離れていて見えず、
        入れた理由も捨てていた。幕は開いたままにする。
      -->
      <ActionResultMessage :text="issueError" type="danger"/>

      <template #footer>
        <CButton color="secondary" :disabled="issuingPass" @click="cancelIssueTemporaryPass">
          やめる
        </CButton>
        <CButton
          class="platform-issue-submit"
          color="primary"
          :disabled="issuingPass || issueMissingFields.length > 0"
          @click="confirmIssueTemporaryPass"
        >出す</CButton>
      </template>
    </CModal>

    <!--
      **出した文字列は、ここでしか出てこない。**DBには引く鍵しか
      入っていない。閉じたら二度と見られない。
    -->
    <CModal
      :show="Boolean(issuedPass)"
      title="一時パスを出しました"
      color="primary"
      :closeOnBackdrop="false"
      @update:show="closeIssuedPass"
    >
      <p>
        <b>この画面を閉じると、二度と見られません。</b>
        電話で口頭でお伝えしてから閉じてください。
      </p>
      <p class="platform-issued-pass">{{ issuedPass }}</p>
      <p v-if="issuedExpiresAt" class="text-muted platform-issued-expires">
        有効期限: {{ formatDateTime(issuedExpiresAt) }}
      </p>
      <p class="text-muted">
        本人にも「一時パスが発行されました」という知らせが届きます
        （<b>文字列そのものは書かれていません</b>）。
      </p>
      <template #footer>
        <CButton color="primary" @click="closeIssuedPass">
          読み上げました
        </CButton>
      </template>
    </CModal>

    <!--
      **プランを追加する／名前と並びを直す。**

      料金の欄は追加のときだけ出す。既存プランの料金を変える道は
      請求管理の「プランの料金」だけで、そちらは適用開始日つきで
      履歴を1本残す。**ここから料金を変えられると、履歴に残らないまま
      単価が変わり、対象月の請求根拠を復元できなくなる。**
    -->
    <CModal
      :show="planModal"
      :title="planForm.planId ? 'プランの名称・表示順を変更' : 'プランを追加'"
      color="primary"
      :closeOnBackdrop="false"
      @update:show="closePlanModal"
    >
      <CRow>
        <CCol v-if="!planForm.planId" sm="12" md="6">
          <label>プランコード<span class="asterisk">*</span></label>
          <CInput
            v-model.trim="planForm.planCode"
            description="半角英数字およびアンダースコア（_）。登録後は変更できません。"
          />
        </CCol>
        <CCol sm="12" md="6">
          <label>プラン名<span class="asterisk">*</span></label>
          <CInput v-model.trim="planForm.planName"/>
        </CCol>
        <CCol sm="12" md="12">
          <CInput label="説明" v-model.trim="planForm.description"/>
        </CCol>
        <CCol sm="12" md="6">
          <CInput
            label="並び順"
            type="number"
            min="0"
            v-model.number="planForm.displayOrder"
            description="数値が小さいほど先に表示されます。"
          />
        </CCol>
      </CRow>

      <template v-if="!planForm.planId">
        <h6 class="form-block-title">料金（月額・税抜）</h6>
        <!--
          **ここで必ず入れてもらう。**料金の記録が無いプランを契約に
          付けると、その会社の請求管理が確定しません。
        -->
        <p class="text-muted">
          基本料と1人あたりの単価は必ず入力してください。
          <b>未入力の場合、このプランの契約は請求管理を確定できません。</b>
          登録後の変更は、「請求管理」＞「プランの料金」から行ってください。
        </p>
        <!--
          **無料プラン（0100）。0円の料金とは別のもの。**0円のままだと
          毎月の確定が見送られ、請求管理の見送りの一覧に出続ける（2026-10-09 から。
          前は毎月0円の請求書が番号付きで作られた）。無料は
          「請求書を作らない」として扱う。料金は0円に固定する。
        -->
        <label class="free-plan-check">
          <input
            type="checkbox"
            :checked="planForm.freePlan"
            @change="toggleFreePlan($event.target.checked)"
          />
          無料プランとして設定する（このプランの契約企業には請求書が発行されません）
        </label>
        <CRow>
          <CCol sm="12" md="6">
            <CInput
              label="基本料"
              type="number"
              min="0"
              :disabled="planForm.freePlan"
              v-model.number="planForm.basePrice"
            />
          </CCol>
          <CCol sm="12" md="6">
            <CInput
              label="1人あたりの単価"
              type="number"
              min="0"
              :disabled="planForm.freePlan"
              v-model.number="planForm.pricePerUser"
            />
          </CCol>
          <!--
            **最低月額（0107）。空欄は「なし」。**最初の料金の記録に一緒に入る。
            無料プランには入れさせない（back も断る）。
          -->
          <CCol sm="12" md="6">
            <CInput
              class="plan-minimum-input"
              label="最低月額"
              type="number"
              min="1"
              :max="minimumMonthlyMax"
              :disabled="planForm.freePlan"
              v-model="planForm.minimumMonthlyAmount"
              description="空欄の場合は最低月額を適用しません。"
            />
          </CCol>
        </CRow>

        <!--
          **人数割の帯（0108）。追加のときだけ**（別の PC の設計 §5「［プランを追加］に帯の入力を足す」）。
          名称・表示順の変更では出さない（既存プランの帯は請求管理の「プランの料金」の［人数割設定］で、
          適用開始日つきで変える）。入れなければ「1名から0%」のままで、割引なしで作られる（P2）。
          **無料プランには入れさせない**（back も R20 で断る）。単価と試算は、この小窓の基本料・単価から。
        -->
        <h6 class="form-block-title plan-volume-discount-title">人数割の帯</h6>
        <p class="text-muted plan-volume-discount-note">人数割の帯を入力しない場合は割引なしで作成されます（作成後に請求管理の「プランの料金」から変更できます）。</p>
        <VolumeDiscountTierEditor
          v-model="planForm.volumeDiscountTiers"
          class="plan-volume-discount"
          :price-per-user="planForm.freePlan ? null : planForm.pricePerUser"
          :base-price="planForm.freePlan ? null : planForm.basePrice"
          :disabled="planForm.freePlan || planBusy"
        />

        <h6 class="form-block-title">含む機能</h6>
        <p class="text-muted">
          既存プランの提供機能をコピーします。<b>コピー元は必ず選んでください。</b>
          コピー後、契約企業ごとの違いは「利用可能機能」タブで調整できます。
        </p>
        <CRow>
          <CCol sm="12" md="6">
            <CSelect
              label="機能のコピー元"
              :options="featureSourceOptions"
              :value="planForm.copyFeaturesFromPlanId"
              @update:value="value => { planForm.copyFeaturesFromPlanId = selectedValue(value) }"
            />
          </CCol>
        </CRow>
      </template>

      <!--
        断られた理由は、押したボタンのすぐ上（幕の本文の最後）に出す（2026-09-26）。
        前は幕の頭に出していて、項目の多い「プランを追加」では、下までたどって
        押した所から見えなかった。
      -->
      <ActionResultMessage :text="planError" type="danger"/>

      <template #footer>
        <CButton color="secondary" :disabled="planBusy" @click="closePlanModal">キャンセル</CButton>
        <CButton color="primary" :disabled="planBusy" @click="submitPlan">
          {{ planForm.planId ? '変更する' : '追加する' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ContractParties from './ContractParties.vue'
import ContractIntroPrice from './ContractIntroPrice.vue'
import ContractMinimumCharge from './ContractMinimumCharge.vue'
import ContractVolumeDiscount from './ContractVolumeDiscount.vue'
import VolumeDiscountTierEditor from './VolumeDiscountTierEditor.vue'
import { hasDiscount, normalizeTiers, tierProblem } from '@/utils/volumeDiscount'
import AccountBillingHistory from './AccountBillingHistory.vue'
import AccountPaymentMethods, { PAYMENT_METHOD_FEATURE_CODES, PAYMENT_METHOD_TEXTS } from './AccountPaymentMethods.vue'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import api from '@/services/accessManagement'
import providerBilling from '@/services/providerBilling'
import { safeErrorDigest } from '../../utils/safeResponseMessage'
import { selectedValue } from '@/utils/coreuiSelect'
import { takeAddPlanRequest } from '@/utils/planAddRequest'
/*
 * **権限の判定は、素の権限文脈から取る。**
 *
 * 同じ名前の api.getAccessContext（services/accessManagement）は、
 * 運営の役割を持つ人へ PLATFORM_SUPER_ADMIN を足して返す。
 * それを canChangeProviderPlan へ渡すと緊急最高管理者として素通りし、
 * **サポート閲覧者にも書くボタンが出る。**押してもサーバは
 * PLAN_CHANGE で断るが、押せる画面を出さない。
 */
import { getAccessContext } from '@/services/accessContext'
import { revealSection } from '@/utils/revealSection'
import { canChangeProviderPlan, canCreateProviderAccount, canOpenProviderBilling, canUpdateProviderContract, canUpdateProviderFeatures } from '@/utils/accessPolicy'

const emptyPlanForm = () => ({
  planId: null,
  planCode: '',
  planName: '',
  description: '',
  displayOrder: 10,
  // **既定は空欄。**0 を入れておくと、触らずに追加して0円の有料プランができる
  // （0円は確定の見送りにかからず、0円の請求書が立つ。2026-09-30 の洗い出し、請求 #6）。
  // 空欄は null で送り、back の「基本料を入力してください。」で断らせる。
  basePrice: '',
  pricePerUser: '',
  copyFeaturesFromPlanId: null,
  // 無料プラン（0100）。請求書を作らない。
  freePlan: false,
  // 最低月額（0107）。空欄は「なし」。
  minimumMonthlyAmount: '',
  // 人数割の帯（0108）。入力の途中は文字列のまま持つ。
  volumeDiscountTiers: noDiscountTiers()
})

/*
 * 人数割の帯の「空」＝「1名から0%」の1行（割引なし）。請求管理の［人数割設定］の小窓・会社の
 * タブ・帯の部品の［割引を設定しない］と同じ出し方。**帯の値（何名から何%）はここに書かない。**
 * 運営が画面で入れ、画面で変える（利用者 2026-09-27「ハードコーディングはやめて」）。
 */
function noDiscountTiers () {
  return [{ fromSeat: '1', discountRate: '0' }]
}

/**
 * 最低月額（0107）の範囲。**1円〜1,000万円。**0円は「なし」と区別が付かない
 * ので受け付けない（なしは空欄）。back と同じ範囲で断る。
 */
const MINIMUM_MONTHLY_MIN = 1
const MINIMUM_MONTHLY_MAX = 10000000

const emptyCreateForm = () => ({
  companyName: '',
  companyEmail: '',
  telephone: '',
  contractContactEmail: '',
  billingContactEmail: '',
  securityContactEmail: '',
  planCode: 'STANDARD',
  contractStatus: 'ACTIVE',
  userLimit: null,
  ownerName: '',
  ownerEmail: '',
  ownerEmployeeId: ''
})

const emptyReplaceInvitationForm = () => ({
  ownerName: '', ownerEmail: '', ownerEmployeeId: ''
})

const STATUS_LABELS = {
  PREPARING: '利用開始前', TRIAL: '無料試用中', ACTIVE: '利用中',
  SUSPENDED: '利用停止中', CANCELLED: '解約済み', EXPIRED: '契約終了'
}

const INVITATION_STATUS_LABELS = {
  PENDING: '受諾待ち', ACCEPTED: '受諾済み', EXPIRED: '期限切れ',
  REVOKED: '取消済み', EMAIL_SEND_FAILED: 'メール未送信'
}

const SOURCE_LABELS = { PLAN: '契約プラン設定', OVERRIDE: '個別設定' }

/**
 * 一時パスの使える時間（分）の選択肢。
 *
 * **選ばせる形にするのは、自由入力だと範囲外を打てるから。**サーバは
 * 決まった範囲しか受け付けないので、範囲外を打った運営には「出せません
 * でした」しか返らない。電話口で本人を待たせたまま、直し方の分からない
 * 行き止まりになる。
 */
const LIFETIME_MINUTE_CHOICES = [15, 30, 60]

/** 何も選ばなくても出せるように、既定を持つ。 */
const DEFAULT_LIFETIME_MINUTES = 30

/*
 * 操作履歴の名前の表は、ここには置かない（2026-09-30、束7）。
 *
 * 前はここに表があったが、コード（ACCOUNT_CREATE など）がサーバの書くコード
 * （ACCOUNT_ONBOARDING_INVITATION_CREATED、CONTRACT_*_UPDATE など）と合わず、
 * ほとんどの行が「設定を変更」「設定情報」と出ていた。名前・対象・変更内容は
 * サーバ（AuditLogPresentation）が付けて返し、書くコードが表に無ければ back の
 * 試験（AuditLogLabelsCoverEveryWrittenCodeTest）が落ちる。
 */

/**
 * 招待のメールが送れなかったか。
 *
 * <p>鍵の名前が2つある。契約企業の作成は {@code invitationEmailSent}、
 * 再送と宛先変更は {@code emailSent}。<b>どちらか片方だけを見ると、見ていない
 * ほうが黙って緑になる。</b>上端の帯（作成）と招待のカード（再送・宛先変更）で
 * 同じ判定を使うため、ここに1つだけ置く。</p>
 */
function emailWasNotSent (result) {
  return !!result
    && (result.emailSent === false || result.invitationEmailSent === false)
}

/**
 * 招待の再送・宛先変更の結果を、招待のカードの下に出す形にする（2026-09-26）。
 *
 * <p><b>通ったが届かなかった、を緑で出さない。</b>上端の帯は黄色で出していたが、
 * 押した所に出す部品には黄色が無い。運営が手を離さないよう、赤（失敗の扱い）で
 * 出す。文はサーバが返したものを優先する。</p>
 */
function invitationResultOf (result, 既定の成功文) {
  return {
    type: emailWasNotSent(result) ? 'danger' : 'success',
    text: (result && result.message) || 既定の成功文
  }
}

export default {
  components: { ContractParties, ContractIntroPrice, ContractMinimumCharge, ContractVolumeDiscount, VolumeDiscountTierEditor, AccountBillingHistory, AccountPaymentMethods, ActionResultMessage },
  name: 'PlatformContractManagement',
  data () {
    return {
      loading: false,
      invitationLoading: false,
      /*
       * **どの会社を選んだ回の応答かを見分ける番号。**
       *
       * 会社を続けて押すと、前の会社の通信が後から返ってくる。
       * 待っている間に selectedAccount は次の会社になっているので、
       * そのまま代入すると **B社の画面にA社の内容が出る**。
       * 選ぶたびに増やして、自分の番号でなくなっていたら捨てる。
       *
       * 招待の記録が無い会社を「エラー」ではなく「記録なし」として
       * 出すようにしたぶん、この取り違えは気づきにくくなった。
       * 前は赤い帯が出るので画面を信用しなかったが、いまは
       * それらしい中身が出てしまう。
       */
      accountRequestSeq: 0,
      // 同じ会社の［管理］の中で、契約・初期特別価格を保存した回数。最低月額のタブが読み直す。
      contractRevision: 0,
      // 「お支払い方法」タブを開いた回数。開くたびに、タブの中身が読み直す。
      paymentMethodsRevision: 0,
      // 「利用可能機能」タブに出す、お支払い方法の許可の行き先の案内（仮の文。差し替える所は1か所）。
      paymentTexts: PAYMENT_METHOD_TEXTS,
      saving: false,
      initialized: false,
      errorMessage: '',
      successMessage: '',
      /* 通ったが届かなかった、を出す帯。成功でも失敗でもない状態がある。 */
      warningMessage: '',
      createModal: false,
      replaceInvitationModal: false,

      /*
       * **幕の中で出す文言は、画面上端のものと分けて持つ。**
       *
       * errorMessage を出す CAlert は画面のいちばん上にあり、モーダルが
       * 開いているあいだは暗幕の下で読めない。この2つは**断られても
       * 幕を開いたままにする**——打った内容を捨てないため——ので、
       * そこへ書くと運営には届かない。押したのに画面が1ドットも
       * 変わらず、押せなかったときと見分けが付かなくなる。
       *
       * 一時パスの確認も同じ（2026-09-26）。前は断られたら幕を閉じて上端へ
       * 出していたが、上端はタブから遠くて見えなかった。issueError へ書く。
       */
      createError: '',
      replaceError: '',
      issueError: '',

      /*
       * **タブの中で押した結果は、押した所に出す**（2026-09-26）。
       * 上端の帯（successMessage など）はタブから200行以上離れていて、
       * 押しても何も起きないように見えた。{ type, text } で持ち、
       * ActionResultMessage へそのまま渡す。会社を選び直したら消す。
       */
      /* ［契約を更新］の結果。ボタンのすぐ下。 */
      contractResult: { type: 'success', text: '' },
      /* 招待の［再送］［招待先を変更］［取消］の結果。招待のカードのすぐ下。 */
      invitationResult: { type: 'success', text: '' },
      /* 利用可能機能の切り替えの結果。キーは機能。そのスイッチの下。 */
      featureResults: {},
      /* ［一時パスを出す］の準備で断られた理由。ボタンのすぐ下。 */
      issueStartError: '',
      accounts: [],

      /**
       * 一時パスの入口が開いているか（場面 S-95）。
       *
       * **サーバが決める。**読めなかったときも false のまま——
       * 出して押させて断られるより、出ないほうがよい。
       */
      temporaryPassEnabled: false,

      /**
       * この会社の利用者。**選ばせるために読む。**
       *
       * 番号を打たせると、桁を1つ間違えただけで同じ会社の別の人へ
       * 正しく発行される。会社の境目はサーバが守るが、**番号自体が
       * 間違っているだけ**なので、そこでは止まらない。
       */
      temporaryPassUsers: [],

      /** 出す相手の利用者ID。**一覧から選んだものだけが入る。** */
      issueUserId: null,

      /** 確認を出している相手。null なら出していない。 */
      issueTarget: null,

      /** なぜ出すのか。**必須。** */
      issueReason: '',

      /** 何分もたせるか。上限と下限はサーバが決める。 */
      issueLifetimeMinutes: DEFAULT_LIFETIME_MINUTES,

      /** いま生きている1本の id。**出すときにそのまま送る。** */
      livePassId: null,

      /** 社内に救える別の人が居るか（**警告のためだけ**）。 */
      alternativeRecoveryAvailable: false,

      /** 出した文字列。**ここでしか出てこない。** */
      issuedPass: '',

      /** 出した1本の期限。 */
      issuedExpiresAt: '',

      /** 一時パスを送っているあいだ true。確認を閉じさせず、［出す］を二度送らない。 */
      issuingPass: false,
      plans: [],

      /**
       * 料金つきのプラン一覧。
       *
       * **取れなくても契約企業一覧は開けるようにする。**この口は
       * 請求の列（0074/0075）が当たっていないと落ちる。本番へ
       * 当たるより先に画面が出ると、契約企業一覧が丸ごと開かなくなる。
       */
      planPricing: [],

      /** 料金を読めたか。読めていないときは金額を出さない。 */
      planPricingLoaded: false,

      /**
       * 料金つきのプラン一覧を読み始めた回数。**いちばん新しく読み始めたものだけを
       * 反映する**（会社を足す・契約を保存する・［再読込］のたびに読み直すので、遅れて
       * 届いた古い一覧で、新しい「契約中」の社数を上書きしない。2026-10-09）。
       */
      planPricingSeq: 0,

      /**
       * 素の権限文脈。
       *
       * **api.getAccessContext は使わない。**あちらは運営の役割を
       * 持つ人へ PLATFORM_SUPER_ADMIN を足して返すので、
       * サポート閲覧者にも書くボタンが出てしまう。
       */
      billingAccessContext: null,

      planModal: false,
      planBusy: false,
      planError: '',
      planForm: emptyPlanForm(),
      minimumMonthlyMax: MINIMUM_MONTHLY_MAX,
      planFields: [
        { key: 'planName', label: 'プラン' },
        { key: 'planCode', label: 'コード' },
        { key: 'basePriceLabel', label: '基本料' },
        { key: 'pricePerUserLabel', label: '1人あたり' },
        // 最低月額（0107）。見出しは請求管理の「プランの料金」の列と同じ語。
        { key: 'minimumMonthlyAmountLabel', label: '最低月額' },
        { key: 'accountCountLabel', label: '契約中' },
        { key: 'actions', label: '操作', sorter: false, filter: false }
      ],

      features: [],
      // 利用可能機能のスイッチを作り直すための番号。CSwitch は自分で印を持つので、
      // 断られたあと値を戻すだけでは印が戻らない（2026-09-22）。
      featureRevision: 0,
      // 利用可能機能の一覧を読み直した回数。**いちばん新しく始めた読み直しだけを反映する**
      // （失敗のあとの読み直しが遅れて届き、後から成功した別の変更を古い表示へ戻していた。2026-09-22 監査）。
      featureListSeq: 0,
      auditLogs: [],
      // 「操作履歴」タブを開いて読み直した回数。**いちばん新しく始めた読み直しだけを反映する**
      // （続けて開いたとき、遅れて届いた古い一覧で新しい一覧を上書きしない。2026-10-09）。
      auditLogListSeq: 0,
      // タブからの読み直しを反映済みの、会社を選んだ回（accountRequestSeq）。その回の
      // ［管理］を開いたときの一覧があとから届いても、読み直した新しい一覧を上書きしない。
      auditLogsReloadedSeq: 0,
      ownerInvitation: null,
      selectedAccount: null,
      createForm: emptyCreateForm(),
      replaceInvitationForm: emptyReplaceInvitationForm(),
      contractForm: {
        accountId: null,
        planId: null,
        contractStatus: 'ACTIVE',
        userLimit: null,
        contractStartDate: '',
        contractEndDate: ''
      },
      /*
        字を 12px にしたら、表の幅に押されて「スタンダー／ド」「2026-10-／09 〜／（期限な／し）」
        「SF3確か／め株式会／社」と値の途中で割れた。短い値の欄（会社数・プラン・状態・人数上限）は
        折らず、会社名の欄は最小の幅を持たせ、契約期間は「日付 〜」と「（期限なし）」の間でだけ折る。
        表が枠より広くなったら横に送り、［管理］の欄は右端に留める（action-cell）。
      */
      accountFields: [
        { key: 'name', label: '会社名', _style: 'min-width: 8em' },
        // **契約企業と販売代理店は、一覧で見えないと意味がない。**
        // どの契約が代理店経由かは、開いて確かめるものではなく
        // 並べて見るもの（手数料を払う相手を数えるため）。
        { key: 'contractorName', label: '契約企業', _style: 'min-width: 8em' },
        { key: 'resellerName', label: '販売代理店', _style: 'min-width: 7em' },
        { key: 'tenantCountLabel', label: '同じ契約の会社数', _classes: 'text-nowrap' },
        { key: 'planName', label: '契約プラン', _classes: 'text-nowrap' },
        { key: 'contractStatusLabel', label: '契約状態', _classes: 'text-nowrap' },
        { key: 'contractPeriodLabel', label: '契約期間' },
        { key: 'userLimitLabel', label: '利用人数上限', _classes: 'text-nowrap' },
        { key: 'actions', label: '', _classes: 'action-cell' }
      ],
      featureFields: [
        { key: 'featureName', label: '機能' },
        { key: 'sourceLabel', label: '設定方法' },
        { key: 'enabled', label: '利用可否' }
      ],
      auditFields: [
        { key: 'createdAt', label: '日時' },
        { key: 'actionLabel', label: '操作内容' },
        { key: 'actorName', label: '操作者' },
        { key: 'targetLabel', label: '対象' },
        /* 前→後の要点（プラン・状態・人数上限・金額など）。サーバが1行にして返す。 */
        { key: 'changeSummary', label: '変更内容' }
      ],
      statusOptions: [
        { value: 'PREPARING', label: '利用開始前' },
        { value: 'TRIAL', label: '無料試用中' },
        { value: 'ACTIVE', label: '利用中' },
        { value: 'SUSPENDED', label: '利用停止中' },
        { value: 'CANCELLED', label: '解約済み' },
        { value: 'EXPIRED', label: '契約終了' }
      ],
      newStatusOptions: [
        { value: 'PREPARING', label: '利用開始前' },
        { value: 'TRIAL', label: '無料試用中' },
        { value: 'ACTIVE', label: '利用中' }
      ]
    }
  },
  computed: {
    /**
     * 終了日の欄に出す説明。
     *
     * **過ぎているときは、それが原因だと分かるように言う。**
     * 期間を外れるとその会社の機能が全部落ちるのに、
     * 画面には理由が出ないままだった。
     */
    endDateWarning () {
      const end = String(this.contractForm.contractEndDate || '').slice(0, 10)
      if (!end) return '未入力の場合は無期限となります。'
      const today = new Date()
      const iso = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0')
      if (end < iso) {
        return '⚠ この日は過ぎています。この会社は全機能が使えません。'
      }
      return 'この日を過ぎると、この会社は全機能が使えなくなります。'
    },

    normalizedAccounts () {
      return this.accounts.map(item => {
        const id = this.value(item, 'accountId', 'account_id', 'id')
        const limit = this.value(item, 'userLimit', 'user_limit')
        const status = this.value(item, 'contractStatus', 'contract_status')
        return {
          raw: item,
          id,
          name: this.value(item, 'name', 'accountName', 'account_name', 'companyName', 'company_name') || '名称未設定の契約企業',
          planId: this.value(item, 'planId', 'plan_id'),
          planCode: this.value(item, 'planCode', 'plan_code'),
          planName: this.value(item, 'planName', 'plan_name') || '未設定',
          contractStatus: status,
          contractStatusLabel: STATUS_LABELS[status] || '未設定',
          contractStartDate: this.dateValue(item, 'contractStartDate', 'contract_start_date'),
          contractEndDate: this.dateValue(item, 'contractEndDate', 'contract_end_date'),
          contractPeriodLabel: this.periodLabel(
            this.dateValue(item, 'contractStartDate', 'contract_start_date'),
            this.dateValue(item, 'contractEndDate', 'contract_end_date')),
          userLimit: limit,
          userLimitLabel: limit == null ? '無制限' : `${limit}名`,

          // **空欄と『無い』を書き分ける。**代理店が空なら直販、
          // 契約企業が空なら契約へまだ結ばれていない、で意味が違う。
          contractorName: this.value(item, 'contractorName', 'contractor_name') || '未登録',
          resellerName: this.value(item, 'resellerName', 'reseller_name') || '直販',
          tenantCountLabel: `${this.value(item, 'tenantCount', 'tenant_count') || 1}社`
        }
      })
    },
    normalizedFeatures () {
      return this.features.map(item => ({
        id: this.value(item, 'id'),
        featureCode: this.value(item, 'featureCode', 'feature_code'),
        featureName: this.value(item, 'featureName', 'feature_name') || '名称未設定の機能',
        enabled: Boolean(this.value(item, 'enabled')),
        sourceLabel: SOURCE_LABELS[this.value(item, 'source')] || '未設定'
      }))
    },
    /**
     * 「利用可能機能」タブに並べる行。**お支払い方法の許可の行は外す**（2026-10-15）。
     *
     * 請求書払い・クレジットカードの許可の入・切は「お支払い方法」タブへ移した。
     * 2つの場所に同じ入・切を置くと、どちらで変えたのか分からなくなる。外すのは画面の
     * 並びだけで、back の一覧の答えと、切り替えの口（updateFeature）はそのまま。
     */
    featuresOnThisTab () {
      return this.normalizedFeatures.filter(feature =>
        !PAYMENT_METHOD_FEATURE_CODES.includes(feature.featureCode))
    },
    /**
     * 操作履歴の行。**名前・対象・変更内容はサーバが付けたものをそのまま出す。**
     *
     * 名前を付けない古いサーバのあいだは「—」にする。コードをそのまま出さない。
     */
    normalizedAuditLogs () {
      return this.auditLogs.map(item => {
        /*
         * **日時は、この画面の招待の日時と同じ書き方で出す**（2026-09-30）。
         * back は機械向けの形（2026-09-30T07:42:59.578+0000）で返すので、
         * そのまま出すと協定世界時の「T」「+0000」が画面に並ぶ。
         * 空のときは空のまま（スマホのカードは「日時未記録」と出す）。
         */
        const createdAt = this.value(item, 'createdAt', 'created_at')
        return {
          createdAt: createdAt ? this.formatDateTime(createdAt) : '',
          actionLabel: this.value(item, 'actionLabel', 'action_label') || '—',
          actorName: this.value(item, 'actorName', 'actor_name') || 'PREIKA運営管理者',
          targetLabel: this.value(item, 'targetLabel', 'target_label') || '—',
          changeSummary: this.value(item, 'changeSummary', 'change_summary') || '—'
        }
      })
    },
    /**
     * プランごとの料金。**id で引く。**
     *
     * 契約のプラン一覧（/access-management/plans）は料金を返さない。
     * 金額は請求側の口（/access-management/billing/plans）から
     * 別に取る。**あちらへ料金の列を足さない**——契約企業一覧が
     * いま本番で使っている口で、請求の列（0074/0075）が無い DB では
     * SQL が実行時に落ちて画面が丸ごと開かなくなる。
     */
    planPricingById () {
      const byId = {}
      for (const row of this.planPricing) {
        const id = this.value(row, 'id')
        if (id === null) continue
        byId[String(id)] = row
      }
      return byId
    },
    /**
     * プランマスターの表。**契約中の社数も一緒に出す。**
     *
     * 料金は全社共通。1つ直すとそのプランの契約企業すべてに効くので、
     * 押す前に何社へ効くのかを見せる。
     */
    planMasterRows () {
      return this.plans.map(plan => {
        const id = this.value(plan, 'id')
        const pricing = this.planPricingById[String(id)] || null
        return {
          id,
          planCode: this.value(plan, 'planCode', 'plan_code') || '',
          planName: this.value(plan, 'planName', 'plan_name') || '名称未設定',
          description: this.value(plan, 'description') || '',
          displayOrder: this.value(plan, 'displayOrder', 'display_order'),
          // 無料プラン（0100）は「無料」と書く。0円とは別（請求書を作らない）。
          basePriceLabel: this.isFreePlan(pricing)
            ? '無料（請求書発行なし）'
            : this.planAmountLabel(pricing, 'basePrice', 'base_price'),
          pricePerUserLabel: this.isFreePlan(pricing)
            ? '無料'
            : this.planAmountLabel(pricing, 'pricePerUser', 'price_per_user'),
          minimumMonthlyAmountLabel: this.minimumMonthlyAmountLabel(pricing),
          accountCountLabel: pricing === null
            ? '—'
            : `${this.value(pricing, 'accountCount', 'account_count') || 0} 社`
        }
      })
    },
    /** 機能の写し元。**いまあるプランからしか選べない。** */
    featureSourceOptions () {
      return this.plans.map(plan => ({
        value: this.value(plan, 'id'),
        label: this.value(plan, 'planName', 'plan_name') || '名称未設定'
      }))
    },
    /**
     * プランを増やす・直せるか。
     *
     * **料金の変更と同じ重さ**（PLAN_CHANGE）。back も同じ権限を
     * 要求する。判定に渡すのは素の権限文脈だけ。
     */
    canEditPlans () {
      return canChangeProviderPlan(this.billingAccessContext)
    },
    /**
     * 契約企業を増やせるか（ACCOUNT_CREATE）。back も同じ権限を要求する。
     *
     * <b>入口のボタンから塞ぐ。</b>中の「作成して招待メールを送信」だけを
     * 押せない形にしても、見るだけの人は幕を開いて入れてから断られる。
     */
    canCreateAccount () {
      return canCreateProviderAccount(this.billingAccessContext)
    },
    /**
     * 利用可能機能を変えられるか（FEATURE_MANAGE）。back も同じ権限を要求する。
     * **見るだけの人には押せる形で出さない**（2026-09-22、閲覧の人で押せて
     * 403 のあと印が戻らなかった）。
     */
    canEditFeatures () {
      return canUpdateProviderFeatures(this.billingAccessContext)
    },
    /**
     * 契約の条件（初期特別価格・契約に関わる会社・代理店手数料）を
     * 変えられるか（CONTRACT_UPDATE）。back は保存を同じ権限で断る。
     * **参照だけの人には押せるボタンを出さない**（監査 F5）。
     */
    canEditContract () {
      return canUpdateProviderContract(this.billingAccessContext)
    },
    /**
     * 請求と入金を見られるか。
     *
     * **開けるのに何もできないタブを出さない。**back は
     * /access-management/billing/receivables を PROVIDER_BILLING_VIEW
     * だけに開いているので、権限の無い人がタブを押すと空と403に
     * なる。利用者からは壊れているのと区別が付かない。
     */
    canSeeBilling () {
      return canOpenProviderBilling(this.billingAccessContext)
    },
    planOptions () {
      return this.plans.map(plan => ({
        value: this.value(plan, 'id'),
        label: this.pricedPlanLabel(plan)
      }))
    },
    planCodeOptions () {
      return this.plans.map(plan => ({
        value: this.value(plan, 'planCode', 'plan_code'),
        label: this.pricedPlanLabel(plan)
      }))
    },
    /**
     * 契約企業の新規作成で、まだ入っていない必須項目の名前。
     *
     * <p>画面に出ている言葉をそのまま使う。中の変数名を出しても、
     * <b>どこを直せばよいか分からない。</b></p>
     */
    missingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**作成ボタンの :disabled は
       * missingFields.length しか見ていない。同じ意味の条件をもう1つ
       * 書くと、片方だけ直したときに「あと0つです」と出ているのに
       * 押せない、というさらに分かりにくい形になる。
       *
       * 3種類の連絡先メールはここに入れない。**未入力なら会社メールで
       * 埋める**ので、足りない項目ではない。
       */
      const missing = []
      if (!this.createForm.companyName) { missing.push('会社名') }
      if (!this.createForm.companyEmail) { missing.push('会社メール') }
      if (!this.createForm.ownerName) {
        missing.push('氏名（最初の契約企業管理者）')
      }
      if (!this.createForm.ownerEmail) {
        missing.push('メールアドレス（最初の契約企業管理者）')
      }
      if (!this.createForm.ownerEmployeeId) {
        missing.push('社員番号（最初の契約企業管理者）')
      }
      return missing
    },

    /**
     * 招待先の変更で、まだ入っていない必須項目の名前。
     *
     * <p><b>社員番号は入れない。</b>利用者の登録・更新では任意で、
     * 受諾後に利用者の更新から入れられる。ここだけ必須にすると、
     * まだ番号の決まっていない担当者へ招待を出し直せない。</p>
     */
    replaceMissingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**変更ボタンの :disabled は
       * replaceMissingFields.length しか見ていない。
       */
      const missing = []
      if (!this.replaceInvitationForm.ownerName) { missing.push('氏名') }
      if (!this.replaceInvitationForm.ownerEmail) {
        missing.push('メールアドレス')
      }
      return missing
    },

    /**
     * 一時パスを出すのに、まだ入っていない必須項目の名前。
     *
     * <p>使える時間は入れない。<b>選択肢から選ぶ形にして、既定の
     * {@link DEFAULT_LIFETIME_MINUTES} 分のまま出せる。</b></p>
     */
    issueMissingFields () {
      /*
       * **押せない条件は、この一覧そのもの。**出すボタンの :disabled は
       * 送っているあいだ（issuingPass）のほかは、issueMissingFields.length しか見ていない。
       */
      const missing = []
      if (!String(this.issueReason || '').trim()) {
        missing.push('理由（記録に残ります）')
      }
      return missing
    },

    /** 使える時間の選択肢。**部品は文字列で扱うので、文字列で渡す。** */
    lifetimeMinuteOptions () {
      return LIFETIME_MINUTE_CHOICES.map(minutes => ({
        value: String(minutes), label: `${minutes}分`
      }))
    },

    /**
     * <b>いま出ている招待が、いま選んでいる会社のものか。</b>
     *
     * 会社を続けて押すと、前の会社の応答が後から届く。代入は世代番号で
     * 止めているが、**止まった直後は「前の会社の招待が出たまま、選んで
     * いるのは次の会社」**という食い違いが残る。その状態で取消を押すと、
     * 画面はA社の招待者を出しているのに、要求はURLの会社ID＝B社へ飛ぶ。
     * 見ているものと動かすものが別になる（2026-09-09、監査で指摘）。
     *
     * だから<b>操作は、出ているものと選んでいるものが一致するときだけ</b>
     * 許す。会社IDを返さない応答は照合できないので、許さない側に倒す。
     */
    invitationMatchesSelectedAccount () {
      if (!this.ownerInvitation || !this.selectedAccount) return false
      const 招待の会社 = this.ownerInvitation.accountId
      if (招待の会社 === null || 招待の会社 === undefined) return false
      return Number(招待の会社) === Number(this.selectedAccount.id)
    },

    canChangeInvitation () {
      return this.invitationIsOperable(
        ['PENDING', 'EMAIL_SEND_FAILED', 'EXPIRED'])
    },

    /**
     * 招待の中身を出せない状態か。
     *
     * **NONE だけを見ていると、招待した相手を無効化した会社で
     * 何も出なくなる。**サーバは INVITED_USER_UNAVAILABLE を返すが、
     * 画面がそれを知らないと下の「招待の状態」カードへ落ちて、
     * 氏名も期限も空の枠だけが出たうえに再送ボタンまで並ぶ。
     */
    ownerInvitationHasNothingToShow () {
      return Boolean(this.ownerInvitation
        && ['NONE', 'INVITED_USER_UNAVAILABLE']
          .includes(this.ownerInvitation.status))
    },
    ownerInvitationNoticeHeading () {
      return this.ownerInvitation
        && this.ownerInvitation.status === 'INVITED_USER_UNAVAILABLE'
        ? '対象ユーザーなし'
        : '招待記録なし'
    },
    ownerInvitationNoticeBadge () {
      return this.ownerInvitation
        && this.ownerInvitation.status === 'INVITED_USER_UNAVAILABLE'
        ? '履歴のみ'
        : '記録なし'
    },
    /** サーバが文言を返さなかったときだけ使う。 */
    ownerInvitationNoticeFallback () {
      return this.ownerInvitation
        && this.ownerInvitation.status === 'INVITED_USER_UNAVAILABLE'
        ? '招待記録は存在しますが、対象の利用者アカウントが有効ではないため詳細を表示できません。'
        : 'この契約企業には招待記録がありません。'
    },
    /**
     * サーバが返した利用者を、画面で使う形へそろえる。
     *
     * <p><b>返ってくるキーは user_id / employee_id。</b>id でも
     * employeeCode でもない。合わせずに書くと value が undefined に
     * なり、<b>一覧は出るのに誰も選べない。</b>実際そうなっていた。</p>
     *
     * <p>いくつかの綴りを見るのは、この口が将来キーを変えたときに
     * 黙って選べなくなるのを避けるため。<b>id が取れない行は捨てる</b>
     * ——選べない選択肢を出しても、押して初めて分かるだけ。</p>
     */
    normalizedTemporaryPassUsers () {
      return this.temporaryPassUsers
        .map(user => ({
          id: this.value(user, 'user_id', 'id', 'userId'),
          name: this.value(user, 'name'),
          email: this.value(user, 'email'),
          employeeCode: this.value(
            user, 'employee_id', 'employeeCode', 'employee_code')
        }))
        .filter(user => user.id !== null && user.id !== undefined)
    },

    /**
     * 選択肢。**氏名とメールアドレスを必ず出す。**
     *
     * <p>番号だけでは人が照合できない。電話の相手と突き合わせられる
     * 形にする。社員番号があれば添える。</p>
     */
    temporaryPassUserOptions () {
      return [{ value: '', label: '選択してください' }].concat(
        this.normalizedTemporaryPassUsers.map(user => ({
          value: String(user.id),
          label: [
            user.name,
            user.email,
            user.employeeCode ? '社員番号 ' + user.employeeCode : null,
            '利用者ID ' + user.id
          ].filter(Boolean).join(' / ')
        }))
      )
    },

    /** いま選ばれている人。**確認画面はこれを出す。** */
    issueTargetUser () {
      if (this.issueTarget === null) { return null }
      return this.normalizedTemporaryPassUsers.find(
        user => Number(user.id) === Number(this.issueTarget)) || null
    },

    canResendInvitation () {
      return this.invitationIsOperable(
        ['PENDING', 'EMAIL_SEND_FAILED', 'EXPIRED'])
    }
  },
  mounted () {
    this.initialize()
    this.loadTemporaryPassStatus()
  },
  methods: {
    /**
     * 契約期間の字を「〜」の後ろで2つの塊に分ける（「2026-10-09 〜」「（期限なし）」）。
     * 塊は折らず、塊と塊のあいだでだけ折る。つなげると元の字と同じ。「〜」が無ければ1つ。
     */
    periodPhrases (label) {
      const text = String(label == null ? '' : label)
      const at = text.indexOf('〜')
      if (at < 0) return [text]
      return [text.slice(0, at + 1), text.slice(at + 1)].filter(part => part.length > 0)
    },

    /**
     * 日付を yyyy-MM-dd で取り出す。
     *
     * サーバは '2026-04-01' のことも '2026-04-01T00:00:00' のことも
     * あるので、頭の10文字だけを見る。**日付入力欄は yyyy-MM-dd しか
     * 受け付けない**ので、時刻が付いたまま入れると欄が空のまま描かれる
     * （入っているのに空に見える）。
     */
    dateValue (item, ...keys) {
      const v = this.value(item, ...keys)
      if (v === null || v === undefined || v === '') return ''
      return String(v).slice(0, 10)
    },

    /** 一覧に出す期間。**空を「無期限」と書き分ける。** */
    periodLabel (start, end) {
      if (!start && !end) return '期限なし'
      if (start && !end) return start + ' 〜（期限なし）'
      if (!start && end) return '〜 ' + end
      return start + ' 〜 ' + end
    },

    /**
     * **テンプレートから使うので methods に置く。**
     * import しただけの関数は、テンプレートからは見えない。
     */
    selectedValue,

    /**
     * 一時パスの入口が開いているかを、画面を開いた時点で読む。
     *
     * <p><b>ここで読まないと、永久に出ない。</b>前は発行の直前に
     * しか読んでいなかったが、その発行ボタンは入口の中にある。
     * サーバを true にしても、運営は一度も一時パスを出せなかった。</p>
     *
     * <p><b>読めなかったら出さない。</b>false でも、enabled が
     * 無くても、403 でも、通信に失敗しても同じ。出して押させて
     * 断られるより、出ないほうがよい。</p>
     */
    /**
     * 一覧から選ぶ。**生の数字は受け取らない。**
     *
     * <p>選択肢に無い値が来たら選ばない。空を選んだら外す。
     * <b>ここを通らずに issueUserId が入る道を残さない。</b></p>
     */
    selectTemporaryPassUser (value) {
      const chosen = this.normalizedTemporaryPassUsers.find(
        user => String(user.id) === String(value))
      this.issueUserId = chosen ? chosen.id : null
    },

    /**
     * この会社の利用者を読む。
     *
     * <p><b>会社を切り替えたら、前の会社の選択を必ず消す。</b>会社Aで
     * 選んだあと会社Bへ切り替えて、古い番号をそのまま送る事故を防ぐ。
     * サーバは会社が合わなければ断るが、<b>そこで初めて気づく形に
     * しない。</b></p>
     *
     * <p>読めなかったら空にする。空なら選べないので、出せない。</p>
     */
    /**
     * 一時パスを出せる相手を読む。
     *
     * seq を渡されたときは、その回の応答かどうかを見て、追い越されて
     * いたら捨てる。**ここが取り違えるといちばん危ない。**別の会社の
     * 利用者一覧に対して一時パスを出しかねない。
     */
    async loadTemporaryPassUsers (seq) {
      this.temporaryPassUsers = []
      this.issueUserId = null
      this.issueTarget = null

      if (!this.selectedAccount) { return }

      const 自分の回 = seq === undefined ? this.accountRequestSeq : seq
      try {
        const users = await api.getUsers(this.selectedAccount.id)
        if (自分の回 !== this.accountRequestSeq) return
        this.temporaryPassUsers = Array.isArray(users) ? users : []
      } catch (error) {
        if (自分の回 !== this.accountRequestSeq) return
        this.temporaryPassUsers = []
      }
    },

    async loadTemporaryPassStatus () {
      try {
        const status = await api.getTemporaryPassStatus()
        this.temporaryPassEnabled = status
          ? status.enabled === true
          : false
      } catch (error) {
        this.temporaryPassEnabled = false
      }
    },

    value (item, ...keys) {
      if (!item) return null
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(item, key) && item[key] !== undefined && item[key] !== null) return item[key]
        const upper = key.toUpperCase()
        if (Object.prototype.hasOwnProperty.call(item, upper) && item[upper] !== undefined && item[upper] !== null) return item[upper]
      }
      return null
    },
    /**
     * 札に出す状態。**この操作で送れたのに記録だけ失敗した回は、受諾待ちとして出す。**
     *
     * 記録に失敗すると、読み直した状態は「メール未送信」になる。そのまま出すと、
     * 本文の「招待メールは送信されましたが…」と食い違う（監査の指摘 K3、2026-09-17）。
     */
    shownInvitationStatus (invitation) {
      if (!invitation) return ''
      /*
       * **直してよいのは「未送信」に見えてしまう未受諾の状態だけ。**受諾済み・取消済み・
       * 期限切れは、送った結果にかかわらずそのまま出す。再送の送信と本人の受諾が
       * 前後すると、受諾済みの招待に SENT_NOT_RECORDED が付いて返ることがある（監査 K3 再指摘）。
       */
      if (invitation.emailDelivery === 'SENT_NOT_RECORDED' && invitation.status === 'EMAIL_SEND_FAILED') {
        return 'PENDING'
      }
      return invitation.status
    },
    invitationStatusLabel (status, emailSent, emailDelivery) {
      if (status === 'PENDING' && !emailSent && emailDelivery !== 'SENT_NOT_RECORDED') return 'メール未送信'
      return INVITATION_STATUS_LABELS[status] || '状態不明'
    },
    invitationStatusColor (status) {
      return {
        PENDING: 'warning', ACCEPTED: 'success', EXPIRED: 'secondary',
        REVOKED: 'danger', EMAIL_SEND_FAILED: 'danger'
      }[status] || 'secondary'
    },
    formatDateTime (value) {
      if (!value) return '未記録'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      }).format(date)
    },
    async initialize () {
      if (this.initialized) return
      this.initialized = true
      try {
        const context = await api.getAccessContext()
        const roles = context && (context.roles || context.ROLES)
        if (!Array.isArray(roles) || !roles.includes('PLATFORM_SUPER_ADMIN')) {
          return this.$router.replace('/dashboard')
        }
        /*
         * **料金と権限は、取れなくても先へ進む。**
         *
         * どちらも自分で例外を握りつぶす。料金の口は請求の列
         * （0074/0075）が無い DB では落ちるし、権限が取れないときは書く
         * ボタンを出さない側へ倒れる。ここで throw させると、
         * **契約の設定そのものができなくなる。**
         */
        await Promise.all([
          this.loadAccounts(),
          this.loadPlans(),
          this.loadPlanPricing(),
          this.loadBillingAccess()
        ])
        this.openAddPlanIfAsked()
      } catch (error) {
        this.handleError(error, '契約企業一覧を表示できませんでした。画面を再読み込みしてください。')
      }
    },
    async loadAccounts () {
      this.loading = true
      this.errorMessage = ''
      try {
        const result = await api.getAccounts()
        if (!Array.isArray(result)) throw new Error('契約企業一覧を読み込めませんでした。')
        this.accounts = result
      } catch (error) {
        this.accounts = []
        this.handleError(error, '契約企業一覧を取得できませんでした。画面を再読み込みしてください。')
        throw error
      } finally {
        this.loading = false
      }
    },
    async loadPlans () {
      const result = await api.getPlans()
      if (!Array.isArray(result)) throw new Error('プラン一覧を読み込めませんでした。')
      this.plans = result
    },
    /**
     * 料金つきのプラン一覧を取る。
     *
     * **落ちても契約企業一覧は開けるままにする。**この口は請求の
     * 列（0074/0075）が無いと実行時に落ちる。本番へ当たるより先に
     * この画面が出ると、金額のせいで契約の設定ごとできなくなる。
     * 金額なしへ倒して、その旨を表の下に出す。
     */
    async loadPlanPricing () {
      // いちばん新しく読み始めたものだけを反映する（下の reloadPlanPricing と同じ番号）。
      this.planPricingSeq = (this.planPricingSeq || 0) + 1
      const mine = this.planPricingSeq
      try {
        const result = await providerBilling.plans()
        if (mine !== this.planPricingSeq) return
        this.planPricing = Array.isArray(result) ? result : []
        this.planPricingLoaded = true
      } catch (error) {
        if (mine !== this.planPricingSeq) return
        this.planPricing = []
        this.planPricingLoaded = false
      }
    },
    /**
     * プランマスターの表（料金と「契約中」の社数）を読み直す。
     *
     * <p>読むのは、画面を開いたときと［プランを追加］のあとだけだった。同じ画面で会社を
     * 足した直後・契約のプランを替えた直後・［再読込］を押したあとも、「契約中」の社数が
     * 前のまま残っていた（2026-10-09、画面で確かめて見つけた。画面を読み込み直すと直る）。</p>
     *
     * <p><b>失敗しても、何も変えない。</b>表は前の姿のまま残す（開いたときの読み込みと
     * 違い、金額を消さない）。会社の一覧の読み直しとは別々に走らせ、こちらの失敗で
     * 会社の一覧を失敗にしない。反映するのは、いちばん新しく読み始めたものだけ。
     * 反映したら true。</p>
     */
    async reloadPlanPricing () {
      this.planPricingSeq = (this.planPricingSeq || 0) + 1
      const mine = this.planPricingSeq
      try {
        const result = await providerBilling.plans()
        if (mine !== this.planPricingSeq) return false
        this.planPricing = Array.isArray(result) ? result : []
        this.planPricingLoaded = true
        return true
      } catch (error) {
        return false
      }
    },
    /**
     * 契約企業の［再読込］。会社の一覧と一緒に、プランマスターの表も読み直す。
     *
     * <p>プランの表は別に走らせる（待たない）。会社の一覧の結果・失敗の出し方は、
     * 今までの［再読込］（loadAccounts）と同じ。</p>
     */
    reloadAccounts () {
      this.reloadPlanPricing()
      return this.loadAccounts()
    },
    /**
     * 書くボタンを出してよいかを、**素の権限文脈**で決める。
     *
     * 取れなければ「出さない」に倒す。閉じる向きに倒すのが正しい。
     */
    async loadBillingAccess () {
      try {
        this.billingAccessContext = await getAccessContext()
      } catch (error) {
        this.billingAccessContext = null
      }
    },
    /**
     * プランを追加する幕を開く。
     *
     * **写し元の既定を入れておく。**含む機能を写さないプランを
     * 作ると、そのプランを付けた会社は全機能が使えなくなる。
     */
    /**
     * 請求管理の「プランの料金」の［プランを追加］から来たら、追加の幕を開いて始める
     * （2026-10-08）。プランマスターは会社の一覧の下にあり、請求管理からは辿れなかった。
     *
     * 合図は1回かぎり（受け取ると消える。読み込み直しても幕は開かない）。
     * **道は書き換えない。**最初は道の目印を読んで外していたが、外すと画面が作り直され、
     * 開いたばかりの幕が消えた。権限が無ければ開かない（ボタンも出ない人。back も断る）。
     */
    openAddPlanIfAsked () {
      if (!takeAddPlanRequest()) return
      if (!this.canEditPlans) return
      this.openAddPlan()
    },
    openAddPlan () {
      this.planForm = emptyPlanForm()
      this.planError = ''
      const sources = this.featureSourceOptions
      if (sources.length) {
        this.planForm.copyFeaturesFromPlanId = sources[0].value
      }
      this.planModal = true
    },
    /** 名前と並びを直す幕を開く。**料金の欄は出さない。** */
    openRenamePlan (row) {
      this.planForm = Object.assign(emptyPlanForm(), {
        planId: row.id,
        planCode: row.planCode,
        planName: row.planName,
        description: row.description,
        displayOrder: row.displayOrder === null ? 0 : row.displayOrder
      })
      this.planError = ''
      this.planModal = true
    },
    closePlanModal () {
      // 送っているあいだは閉じない（2026-09-26 監査）。閉じると、あとから届いた理由を出す場所がなくなる。
      if (this.planBusy) return
      this.planModal = false
      this.planError = ''
    },
    /**
     * プランを保存する。
     *
     * **料金はここでは送らない**（新規のときだけ、最初の1本として
     * 送る）。既存プランの料金を変える道は請求管理の画面だけで、
     * そちらは適用開始日つきで履歴を1本残す。
     */
    async submitPlan () {
      if (this.planBusy) return
      const minimumMonthlyAmount = this.planForm.planId
        ? null : this.minimumMonthlyAmountToSend()
      if (minimumMonthlyAmount === undefined) {
        // **送らずに止める。**back と同じ文で断る（範囲は 1円〜1,000万円の整数）。
        this.planError = '最低月額は1円〜10,000,000円の範囲内で、整数（小数点なし）で入力してください。'
        return
      }
      const volumeDiscountTiers = this.planForm.planId
        ? null : this.volumeDiscountTiersToSend()
      if (volumeDiscountTiers === undefined) {
        // **送らずに止める。**帯の断りは請求管理の［人数割設定］と同じ検査・同じ文（V15〜V19）。
        this.planError = tierProblem(this.planForm.volumeDiscountTiers)
        return
      }
      this.planBusy = true
      this.planError = ''
      let saved = false
      try {
        const form = this.planForm.planId
          ? {
            planId: this.planForm.planId,
            planName: this.planForm.planName,
            description: this.planForm.description,
            displayOrder: this.planForm.displayOrder
          }
          : {
            planCode: this.planForm.planCode,
            planName: this.planForm.planName,
            description: this.planForm.description,
            displayOrder: this.planForm.displayOrder,
            basePrice: this.planForm.basePrice === '' ? null : this.planForm.basePrice,
            pricePerUser: this.planForm.pricePerUser === '' ? null : this.planForm.pricePerUser,
            copyFeaturesFromPlanId: this.planForm.copyFeaturesFromPlanId,
            freePlan: this.planForm.freePlan === true,
            // 最低月額（0107）。**新規のときだけ。**改名の口からは料金と同じく変えない。
            minimumMonthlyAmount,
            /*
             * 人数割の帯（0108）。**新規のときだけ。**[{ fromSeat, discountRate }]（数・人数の昇順）。
             * 割引なし（「1名から0%」だけ・無料プラン）は null（帯の記録を作らない＝割引なし、P2）。
             * 鍵の名前は back の［プランを追加］の口（PlanMasterService.add）が受ける volumeDiscountTiers。
             * back は null・空の並びを「帯なし」として受け、帯は保存の口と同じ検査・同じ文で断る。
             */
            volumeDiscountTiers
          }
        await providerBilling.savePlan(form)
        saved = true
        /*
         * **並べ直したものを、押した人がその場で見られるように。**
         * 料金も取り直す（新しいプランは料金つきで増えている）。
         */
        await Promise.all([this.loadPlans(), this.loadPlanPricing()])
        this.planModal = false
      } catch (error) {
        if (saved) {
          /*
           * **保存は通っている**（2026-09-27 監査）。あとの一覧の読み直しで落ちたのを
           * 「保存できませんでした」と言うと、押し直して同じプランが二つできる。
           * 幕を閉じ、読み込みの失敗として上端に出す（画面を開いたときと同じ文）。
           */
          this.planModal = false
          this.handleError(error, '契約企業一覧を表示できませんでした。画面を再読み込みしてください。')
          return
        }
        this.planError = this.failureText(
          error, 'プランを保存できませんでした。時間をおいて再度お試しください。')
      } finally {
        this.planBusy = false
      }
    },
    /** 無料プランか（0100）。料金の口が返す freePlan を見る。 */
    isFreePlan (pricing) {
      return !!pricing && this.value(pricing, 'freePlan', 'free_plan') === true
    },
    /** 無料にしたら料金を0円に固定する。外したら入力に戻す。 */
    toggleFreePlan (checked) {
      this.planForm.freePlan = checked === true
      if (this.planForm.freePlan) {
        this.planForm.basePrice = 0
        this.planForm.pricePerUser = 0
        // 無料プランに最低月額は入れられない（0107）。
        this.planForm.minimumMonthlyAmount = ''
        // 無料プランに人数割は入れられない（0108。back も R20 で断る）。帯は「割引なし」へ戻す。
        this.planForm.volumeDiscountTiers = noDiscountTiers()
      }
    },
    /**
     * 追加で送る人数割の帯（0108）。**割引なし（率が全部0）と無料プランは null**（帯の記録を作らない）。
     * 帯に断りがあれば undefined（送らずに止める。断りの文は tierProblem）。
     * **検査は請求管理の［人数割設定］と同じ関数**（src/utils/volumeDiscount.js）。
     */
    volumeDiscountTiersToSend () {
      if (this.planForm.freePlan === true) return null
      const tiers = this.planForm.volumeDiscountTiers
      if (tierProblem(tiers)) return undefined
      return hasDiscount(tiers) ? normalizeTiers(tiers) : null
    },
    /**
     * 追加で送る最低月額（0107）。**空欄は null（なし）。無料プランはいつも null。**
     * 範囲の外か、整数でないなら undefined（送らずに止める）。
     * **小数は送らない**（front #784 の監査。請求書の明細の合計と小計が1円ずれる）。
     */
    minimumMonthlyAmountToSend () {
      if (this.planForm.freePlan === true) return null
      const text = String(this.planForm.minimumMonthlyAmount === null
        || this.planForm.minimumMonthlyAmount === undefined
        ? '' : this.planForm.minimumMonthlyAmount).trim()
      if (text === '') return null
      const amount = Number(text)
      if (!Number.isInteger(amount)
        || amount < MINIMUM_MONTHLY_MIN || amount > MINIMUM_MONTHLY_MAX) {
        return undefined
      }
      return amount
    },
    /**
     * プランマスターの表の最低月額（0107）。
     *
     * **読めていないときは「—」。**読めていて入っていないとき（無料プランを
     * 含む）は「なし」の語。最低月額が無いプランは、料金の口が鍵ごと
     * 返さないことがある（MyBatis の map は NULL の列を入れない）。
     */
    minimumMonthlyAmountLabel (pricing) {
      if (!pricing) return '—'
      const amount = this.isFreePlan(pricing)
        ? null
        : this.value(pricing, 'minimumMonthlyAmount', 'minimum_monthly_amount')
      if (amount === null) return '設定なし'
      return this.yen(amount)
    },
    /** 金額の見せ方。**読めていないときは「—」。0円とは書き分ける。** */
    planAmountLabel (pricing, camelKey, snakeKey) {
      if (!pricing) return '—'
      const amount = this.value(pricing, camelKey, snakeKey)
      if (amount === null) return '—'
      return this.yen(amount)
    },
    yen (amount) {
      const number = Number(amount)
      if (!Number.isFinite(number)) return '—'
      return `${number.toLocaleString('ja-JP')}円`
    },
    /**
     * プルダウンに出す名前。**いくらのプランかをその場で見せる。**
     *
     * 契約を設定する人は、名前だけでは「月20万円のプラン」を選べて
     * いるかどうか分からない。金額が取れないときは、今までどおり
     * 名前だけにする（**読めていないのに0円と書かない**）。
     *
     * **最低月額（0107）はここに足さない。**会社ごとに「当てない／別の金額」が
     * 選べるので、プランの最低月額はその会社で請求する額とは限らない。
     * プランの値はプランマスターの表に、その会社の値は会社の［管理］の
     * 最低月額のタブに出す。
     */
    pricedPlanLabel (plan) {
      const name = this.value(plan, 'planName', 'plan_name') || '名称未設定'
      const pricing = this.planPricingById[String(this.value(plan, 'id'))]
      if (!pricing) return name
      const base = this.value(pricing, 'basePrice', 'base_price')
      const perUser = this.value(pricing, 'pricePerUser', 'price_per_user')
      if (base === null || perUser === null) return name
      return `${name}（基本料 ${this.yen(base)} / 1人 ${this.yen(perUser)}）`
    },
    openCreate () {
      this.createForm = emptyCreateForm()
      /*
       * **既定のプランは、いま選べるものから選ぶ。**
       *
       * emptyCreateForm は 'STANDARD' を直書きしている。プランの
       * 名前やコードを画面から変えられるようになると、STANDARD が
       * 無い環境が出てくる。そのまま送ると、作成が
       * 「指定された契約プランが存在しません。」で落ちる——
       * **画面のプルダウンには出ていないコードを送っている**ので、
       * 押した人には理由が分からない。
       */
      const codes = this.planCodeOptions.map(one => one.value)
      if (codes.length && !codes.includes(this.createForm.planCode)) {
        this.createForm.planCode = codes[0]
      }
      /* **前回断られた文言を残さない。**まだ何もしていないのに赤い字が
         出ている画面から始まってしまう。 */
      this.createError = ''
      this.createModal = true
    },
    /*
     * 送っているあいだは閉じない（2026-09-26 監査）。キャンセルも見出しの×もここへ来る。
     * 閉じると、あとから届いた理由を出す場所がなくなる。
     */
    closeCreate () {
      if (this.saving) return
      this.createModal = false
    },
    /**
     * 足りないまま送らないための最後の砦。
     *
     * <p>見るのは <code>missingFields</code> ひとつだけ。<b>ここに条件を
     * もう1組書くと、画面の一覧と食い違う。</b></p>
     *
     * <p>画面ではボタンが押せないので、ここへ来るのは画面を通らない
     * 呼び出しだけ。足りない項目は幕の中の一覧に、押す前から出ている。</p>
     */
    validateCreate () {
      if (!this.missingFields.length) { return }
      throw new Error(this.missingFields.join('、') + 'を入力してください。')
    },

    /**
     * サーバへ送る形を作る。
     *
     * <p><b>連絡先は、未入力なら会社メールで埋める。</b>契約タブに連絡先の
     * 入力欄は無く、契約の更新はプラン・契約状態・利用人数上限しか
     * 受け取らない。つまり<b>ここで入れそこねると、あとから画面では
     * 直せない。</b>空で作るより、届く先が1つでも残っているほうがよい。</p>
     */
    createPayload () {
      const form = this.createForm
      const companyEmail = String(form.companyEmail || '').trim()
      const orCompanyEmail = value =>
        String(value || '').trim() || companyEmail
      return {
        ...form,
        contractContactEmail: orCompanyEmail(form.contractContactEmail),
        billingContactEmail: orCompanyEmail(form.billingContactEmail),
        securityContactEmail: orCompanyEmail(form.securityContactEmail)
      }
    },
    async createAccount () {
      this.saving = true
      this.errorMessage = ''
      this.createError = ''
      try {
        this.validateCreate()
        const result = await api.createAccount(this.createPayload())
        this.createModal = false
        this.announceInvitationResult(
          result,
          '契約企業を作成し、最初の契約企業管理者へ招待メールを送信しました。')
        /*
         * 会社が増えると、プランマスターの「契約中」の社数も変わる。会社の一覧と
         * 一緒に読み直す（プランの表の読み直しは失敗しても何も投げない）。
         */
        await Promise.all([this.loadAccounts(), this.reloadPlanPricing()])
      } catch (error) {
        /*
         * **幕の中へ書く。**断られても幕は開いたまま——打った内容を
         * 捨てないため——なので、画面上端へ書くと暗幕の下で読めない。
         * 運営から見ると押しても画面が1ドットも変わらず、**押せなかった
         * ときと見分けが付かない。**
         */
        this.createError = this.failureText(
          error, '契約企業を作成できませんでした。時間をおいて再度お試しください。')
      } finally {
        this.saving = false
      }
    },
    /**
     * 出す前の確認を開く（場面 S-95）。
     *
     * <p><b>開く前にサーバへ聞く。</b>いま生きている1本があるか、
     * 社内に救える別の人が居るか、入口が開いているか。どれも
     * <b>サーバが決める</b>ので、画面で数えない。</p>
     *
     * <p>聞けなかったら開かない。分からないまま出すと、読み上げ中の
     * 1本を黙って殺すかもしれない。</p>
     */
    async startIssueTemporaryPass () {
      if (!this.selectedAccount || !this.issueUserId) { return }

      /*
       * **一覧に居る人だけ。**選択肢を通らずに値が入っていたら進まない。
       * 番号を直接書き換えられても、ここで止まる。
       */
      const chosen = this.normalizedTemporaryPassUsers.find(
        user => Number(user.id) === Number(this.issueUserId))
      if (!chosen) { return }
      this.issueReason = ''
      this.issueLifetimeMinutes = DEFAULT_LIFETIME_MINUTES
      this.issuedPass = ''
      this.issuedExpiresAt = ''
      this.issueStartError = ''
      this.issueError = ''
      /*
       * **押したときの会社と社員を控える**（2026-09-26 監査）。確認の幕はまだ開いて
       * いないので、聞いているあいだに一覧から別の会社や、選択欄で別の社員を選べる。
       * 替わっていたら、前の相手の答え（生きている1本・断られた理由）を書かない。
       * 前は返ってきたあとの issueUserId を相手にしていたので、A さんの生きている
       * 1本を持ったまま B さんの確認が開いた。
       */
      const 対象 = this.selectedAccountTarget()
      const 相手 = chosen.id
      const まだ同じ = () => this.stillOnInvitationTarget(対象)
        && Number(this.issueUserId) === Number(相手)

      try {
        const state = await api.getTemporaryPassState(
          this.selectedAccount.id, 相手)
        if (!まだ同じ()) return
        this.livePassId = state.livePassId === undefined
          ? null
          : state.livePassId
        this.alternativeRecoveryAvailable =
          Boolean(state.alternativeRecoveryAvailable)

        /*
         * **開閉はここで決めない。**入口を出すかどうかは、画面を
         * 開いた時点の status で決まっている。ここで決めると、その
         * 入口を押さないと値が入らない——入口が出ないと押せないので、
         * 輪になる。
         *
         * 開いたまま閉じられた場合だけ、ここで消す。
         */
        if (state.enabled === false) {
          this.temporaryPassEnabled = false
          this.issueTarget = null
          return
        }

        this.issueTarget = 相手
      } catch (error) {
        /*
         * **黙って戻らない。**前はここで確認を開かないだけだった。
         * 運営から見ると、押しても画面が1ドットも変わらない——電話口で
         * 本人を待たせたまま、押せていないのか壊れているのかが分からない。
         *
         * 理由は［一時パスを出す］のすぐ下に出す（2026-09-26）。前は画面上端で、
         * タブから遠くて見えなかった。
         */
        if (!まだ同じ()) return
        this.issueTarget = null
        this.issueStartError = this.failureText(error, '一時パスを発行する準備ができませんでした。')
      }
    },

    /**
     * 使える時間を選ぶ。
     *
     * <p><b>選択肢の外は受け取らない。</b>部品は文字列で返すので数へ直す。
     * 直さずに送ると "30" のまま届き、受ける側の型次第で断られる。</p>
     */
    selectLifetimeMinutes (value) {
      const minutes = Number(value)
      this.issueLifetimeMinutes = LIFETIME_MINUTE_CHOICES.indexOf(minutes) >= 0
        ? minutes
        : DEFAULT_LIFETIME_MINUTES
    },

    /** 出すのをやめる。**打ちかけを残さない。** */
    cancelIssueTemporaryPass () {
      /*
       * 送っているあいだは閉じない（2026-09-26 監査）。背景・×・［やめる］の全部がここへ来る。
       * 閉じると、あとから届いた理由を出す場所がなくなる。
       */
      if (this.issuingPass) return
      this.issueTarget = null
      this.issueReason = ''
      this.issueError = ''
      this.livePassId = null
      this.alternativeRecoveryAvailable = false
    },

    /**
     * 出す（場面 S-95）。
     *
     * <p><b>出す前に読んだ id を、そのまま送る。</b>送らないと、2人の
     * 運営が同時に出したときに、あとから通ったほうが先に渡した1本を
     * 黙って殺す。</p>
     */
    async confirmIssueTemporaryPass () {
      // 送っているあいだに［出す］をもう一度押されても、2回目は送らない（2026-09-26 監査）。
      if (this.issuingPass) { return }
      const target = this.issueTarget
      if (!target || !this.selectedAccount) { return }
      /*
       * 見るのは issueMissingFields ひとつだけ。**ここに条件をもう1組
       * 書くと、幕の中の一覧と食い違う。**
       */
      if (this.issueMissingFields.length) { return }
      const reason = String(this.issueReason || '').trim()
      this.issueError = ''
      this.issuingPass = true

      try {
        const issued = await api.issueTemporaryPass(
          this.selectedAccount.id, target, {
            reason: reason,
            lifetimeMinutes: this.issueLifetimeMinutes,
            expectedLivePassId: this.livePassId
          })
        this.issuedPass = issued.pass || ''
        this.issuedExpiresAt = issued.expiresAt || ''
        this.issueReason = ''
      } catch (error) {
        /*
         * **黙って閉じない。**前は確認を閉じるだけだったので、運営には
         * 出たのか失敗したのかが分からなかった。**電話の相手を待たせた
         * まま**、読み上げる文字列も、断られた理由も出てこない。
         *
         * **確認は閉じない**（2026-09-26）。理由は幕の中（issueError）に出す。
         * 前は幕を閉じて画面上端へ出していたが、上端はタブから遠くて見えず、
         * 入れた理由も捨てていた。やり直すときは［やめる］で閉じて、もう一度
         * ［一時パスを出す］から入る（いま生きている1本を読み直すため）。
         */
        this.issueError = this.failureText(error, '一時パスを出せませんでした。')
      } finally {
        this.issuingPass = false
      }
    },

    /**
     * 出した文字列を閉じる。
     *
     * <p><b>持ち物からも消す。</b>画面から消しただけだと、開発者の
     * 道具から見えたままになる。</p>
     */
    closeIssuedPass () {
      this.issuedPass = ''
      this.issuedExpiresAt = ''
      this.issueTarget = null
      this.livePassId = null
      this.alternativeRecoveryAvailable = false
    },

    async selectAccount (item) {
      /*
       * **会社を選び直したら、前の会社の帯は赤も緑も消す。**
       *
       * ここだけ errorMessage を消していなかった（loadAccounts も
       * createAccount も消している）。エラー帯は画面のいちばん上に出る
       * ので、1社で失敗すると、正常に開ける別の会社を選んでも赤い帯が
       * 残り続ける。テスターに「他のテナントも管理できなくなる」と
       * 見えたのはこれ。実際には他社のデータは読めていた。
       *
       * **緑の帯も同じだった。**赤だけ直して緑を見落としていた。
       * A社で「招待を取消」して緑帯が出たまま一覧でB社を押すと、
       * 赤帯は消えるのに「招待を取消しました。」だけが残る。B社の招待は
       * PENDING のまま出ているので、担当者は<b>B社の取消が効いていない</b>
       * と読む。取消は招待リンクを殺す操作なので、押し直しへ誘導する。
       * 緑帯を消すのは画面上端の閉じるボタンだけで、時間でも消えない。
       */
      this.errorMessage = ''
      this.successMessage = ''
      /* 黄帯も同じ理由で消す。A社の「届いていません」がB社の画面に残ると、
         B社の招待まで届いていないと読まれる。 */
      this.warningMessage = ''
      /* タブの中に出した結果も同じ理由で消す（2026-09-26）。
         A社の「招待を取消しました。」がB社のタブに残ると、B社の取消に読める。 */
      this.contractResult = { type: 'success', text: '' }
      this.invitationResult = { type: 'success', text: '' }
      this.featureResults = {}
      this.issueStartError = ''
      this.issueError = ''
      this.selectedAccount = item
      /*
       * **開くだけでなく、そこへ連れて行く。**節は一覧のずっと下に生えるので、
       * 画面が動かないと「押しても何も起きない」に見える（スマホでは特に）。
       */
      revealSection(this, 'selectedAccountCard')
      this.contractForm = {
        accountId: item.id,
        planId: item.planId,
        contractStatus: item.contractStatus || 'ACTIVE',
        userLimit: item.userLimit,
        contractStartDate: item.contractStartDate,
        contractEndDate: item.contractEndDate
      }
      this.ownerInvitation = null
      /*
       * **他の中身も、その場で空にする。**
       *
       * 招待だけ null にしていた。機能一覧は前の会社のものが残ったまま
       * 描かれ続けるので、切り替えた直後にスイッチを押すと
       * <b>A社の機能コードを、B社に対して書き込む</b>（changeFeature の
       * 宛先は selectedAccount.id、押した項目は前の会社の一覧から来る）。
       * 招待の取り違えと同じ形で、こちらは書き込みなので実害が大きい。
       *
       * 一時パスの相手も同じ。消していたのは loadTemporaryPassUsers の
       * 冒頭だけで、そこは<b>機能一覧の取得が終わってからしか通らない</b>。
       * 取得に失敗すると catch が赤帯を出して終わるので、ヘッダは B社
       * なのに一時パスの相手は A社の社員のまま残る。世代番号は追い越し
       * だけを見ていて、失敗を見ていなかった。
       */
      this.features = []
      this.auditLogs = []
      this.temporaryPassUsers = []
      this.issueUserId = null
      this.issueTarget = null
      const seq = ++this.accountRequestSeq
      try {
        const [features, auditLogs] = await Promise.all([
          api.getFeatures(item.id),
          api.getAuditLogs(item.id)
        ])
        /* 待っている間に別の会社が選ばれていたら、この応答は捨てる。 */
        if (seq !== this.accountRequestSeq) return
        this.features = Array.isArray(features) ? features : []
        /*
         * 待つあいだに「操作履歴」タブを開いての読み直しが先に届いていたら、
         * あとから届いたこの一覧（先に読み始めた古いほう）で上書きしない。
         */
        if (this.auditLogsReloadedSeq !== seq) {
          this.auditLogs = Array.isArray(auditLogs) ? auditLogs : []
        }
        await this.loadOwnerInvitation(seq)
        if (seq !== this.accountRequestSeq) return
        await this.loadTemporaryPassUsers(seq)
      } catch (error) {
        if (seq !== this.accountRequestSeq) return
        /*
         * **回転も落とす。**招待の読込み中の印を消すのは
         * loadOwnerInvitation の finally だけだが、ここへ落ちると
         * その関数は一度も呼ばれない。前の会社の読込みが走っていた
         * 場合、そちらの finally も世代が違うので素通りする。
         * <b>誰も消さないまま、回転だけが残る。</b>タブは
         * v-if="invitationLoading" で丸ごとスピナーに差し替わるので、
         * 次にどれかの会社をうまく開くまで中身も操作も出ない。
         *
         * 追い越された側に世代の判定を入れた副作用で生まれた穴で、
         * それを入れる前は起きなかった（無条件に消していたため）。
         */
        this.invitationLoading = false
        this.handleError(error, '契約企業の詳細を取得できませんでした。画面を再読み込みしてください。')
      }
    },
    /**
     * 招待の状態を読む。
     *
     * seq を渡されたときは、その回の応答かどうかを見て、追い越されて
     * いたら捨てる。再送や取消のあとに呼ぶときは渡さない（そのときは
     * 選んでいる会社が変わっていないので、待つ必要がない）。
     */
    async loadOwnerInvitation (seq) {
      if (!this.selectedAccount) return
      const 自分の回 = seq === undefined ? this.accountRequestSeq : seq
      this.invitationLoading = true
      try {
        const status = await api.getOwnerInvitation(this.selectedAccount.id)
        if (自分の回 !== this.accountRequestSeq) return
        this.ownerInvitation = status
      } catch (error) {
        if (自分の回 !== this.accountRequestSeq) return
        this.ownerInvitation = null
        this.handleError(error, '最初の契約企業管理者の招待状態を取得できませんでした。画面を再読み込みしてください。')
      } finally {
        /*
         * **ここも古い回では触らない。**追い越された側が読込み中の印を
         * 消すと、いま走っている新しい読込みの回転が止まり、まだ届いて
         * いない中身が「出そろった」ように見える。
         */
        if (自分の回 === this.accountRequestSeq) {
          this.invitationLoading = false
        }
      }
    },

    /**
     * その状態の招待を、いまボタンとして出してよいか。
     *
     * 状態だけでは足りない。**出ている招待と選んでいる会社が一致すること**が要る。
     *
     * ここに「読込み中なら出さない」は書かない。**書いても一度も通らない。**
     * このタブは invitationLoading の間は節ごと差し替わって回転だけを出すので、
     * ボタンはそもそも描かれない。通らない条件を置くと、壊しても落ちない
     * 検査ができあがる（実際そうなった）。読込み中の入口は
     * invitationOperationTarget で塞ぐ。
     */
    invitationIsOperable (状態の一覧) {
      if (!this.invitationMatchesSelectedAccount) return false
      return 状態の一覧.includes(this.ownerInvitation.status)
    },

    /**
     * いま選んでいる会社を、操作の宛先として固定する。
     *
     * <b>操作の途中で会社を切り替えられる。</b>開始時に会社IDと世代を
     * 控えておかないと、返ってきた結果を別の会社の画面へ書き込む。
     */
    selectedAccountTarget () {
      if (!this.selectedAccount) return null
      return {
        accountId: this.selectedAccount.id,
        seq: this.accountRequestSeq
      }
    },

    /**
     * 招待を動かす前に、宛先を1つに固定する。
     *
     * 会社の固定に加えて、<b>出ている招待がその会社のものであること</b>と
     * 読み直しの途中でないことを見る。
     */
    invitationOperationTarget () {
      if (!this.invitationMatchesSelectedAccount) return null
      if (this.invitationLoading) return null
      return this.selectedAccountTarget()
    },

    /** その操作を始めたときの会社を、まだ選んでいるか。 */
    stillOnInvitationTarget (対象) {
      if (!対象 || !this.selectedAccount) return false
      if (対象.seq !== this.accountRequestSeq) return false
      return Number(this.selectedAccount.id) === Number(対象.accountId)
    },

    async resendInvitation () {
      const 対象 = this.invitationOperationTarget()
      if (!対象) return
      this.saving = true
      this.invitationResult = { type: 'success', text: '' }
      try {
        const status = await api.resendOwnerInvitation(対象.accountId)
        if (!this.stillOnInvitationTarget(対象)) return
        this.ownerInvitation = status
        this.invitationResult = invitationResultOf(status, '招待メールを再送しました。')
      } catch (error) {
        if (!this.stillOnInvitationTarget(対象)) return
        this.invitationResult = {
          type: 'danger',
          text: this.failureText(error, '招待メールを再送できませんでした。時間をおいて再度お試しください。')
        }
      } finally {
        this.saving = false
      }
    },
    openReplaceInvitation () {
      this.replaceInvitationForm = {
        ownerName: this.ownerInvitation && this.ownerInvitation.invitedName ? this.ownerInvitation.invitedName : '',
        ownerEmail: '',
        ownerEmployeeId: ''
      }
      /* **前回断られた文言を残さない。**開いた瞬間に赤い字が出ていると、
         いま断られたのだと読める。 */
      this.replaceError = ''
      this.replaceInvitationModal = true
    },
    // 新規作成と同じ。送っているあいだは閉じない。
    closeReplaceInvitation () {
      if (this.saving) return
      this.replaceInvitationModal = false
    },
    async replaceInvitation () {
      const 対象 = this.invitationOperationTarget()
      if (!対象) return
      /*
       * 見るのは replaceMissingFields ひとつだけ。**ここに条件をもう1組
       * 書くと、幕の中の一覧と食い違う。**画面ではボタンが押せないので、
       * ここへ来るのは画面を通らない呼び出しだけ。足りない項目は、押す
       * 前から幕の中に出ている。
       */
      if (this.replaceMissingFields.length) { return }
      this.saving = true
      this.replaceError = ''
      try {
        const status = await api.replaceOwnerInvitation(
          対象.accountId, this.replaceInvitationForm)
        if (!this.stillOnInvitationTarget(対象)) return
        this.ownerInvitation = status
        this.replaceInvitationModal = false
        this.invitationResult = invitationResultOf(
          status, '招待先を変更し、新しい招待メールを送信しました。')
      } catch (error) {
        if (!this.stillOnInvitationTarget(対象)) return
        /*
         * **幕の中へ書く。**断られても幕は開いたままなので、画面上端では
         * 暗幕の下になる。ここは<b>いま使えている招待リンクを捨てる</b>
         * 操作——通ったのか断られたのかが分からないまま放置されると、
         * 新しい担当者にも古い担当者にも届かない状態を疑えない。
         */
        this.replaceError = this.failureText(
          error, '招待先を変更できませんでした。時間をおいて再度お試しください。')
      } finally {
        this.saving = false
      }
    },
    async revokeInvitation () {
      const 対象 = this.invitationOperationTarget()
      if (!対象) return
      /*
       * **どの会社の、誰宛ての招待を消すのかを書く。**
       * 「現在の招待リンク」だけだと、画面に出ているものと要求先が
       * 食い違っていても気づけない。会社名と宛先を確認文に出せば、
       * 押す直前に人が止められる（2026-09-09、監査で指摘）。
       */
      if (!window.confirm(this.revokeInvitationConfirmText())) return
      this.saving = true
      this.invitationResult = { type: 'success', text: '' }
      try {
        const status = await api.revokeOwnerInvitation(対象.accountId)
        if (!this.stillOnInvitationTarget(対象)) return
        this.ownerInvitation = status
        this.invitationResult = { type: 'success', text: '招待を取消しました。' }
      } catch (error) {
        if (!this.stillOnInvitationTarget(対象)) return
        this.invitationResult = {
          type: 'danger',
          text: this.failureText(error, '招待を取り消せませんでした。時間をおいて再度お試しください。')
        }
      } finally {
        this.saving = false
      }
    },

    /**
     * 選んでいる会社の名前。
     *
     * **一覧の行が持つのは name。**companyName という項目は無い
     * （normalizedAccounts がいくつかの綴りを name に寄せている）。
     * 最初は selectedAccount.companyName を見ていて、実機では
     * いつも「選択中の契約企業」としか出なかった。単体検査は、私が
     * companyName を持つ会社を自分で作って渡していたので緑だった
     * （2026-09-09、ブラウザで確認して気づいた）。
     *
     * 招待の応答は companyName を返すので、そちらを先に見る。
     */
    selectedAccountName () {
      if (this.ownerInvitation && this.ownerInvitation.companyName) {
        return this.ownerInvitation.companyName
      }
      const 会社 = this.selectedAccount
      if (!会社) return ''
      return 会社.name || 会社.companyName || ''
    },

    /** 取消の確認文。**会社名と宛先を必ず入れる。** */
    revokeInvitationConfirmText () {
      const 会社 = this.selectedAccountName() || '選択中の契約企業'
      const 宛先 = this.ownerInvitation && this.ownerInvitation.invitedName
        ? this.ownerInvitation.invitedName : '氏名未設定'
      return '「' + 会社 + '」の招待（宛先: ' + 宛先 + '）を無効にします。'
        + 'この招待リンクは使えなくなります。よろしいですか？'
    },
    async saveContract () {
      /*
       * **押したときの会社を控える**（2026-09-26 監査）。結果は選んでいる会社の
       * タブの中に出すので、待つあいだに別の会社へ切り替えると、前の会社の
       * 「更新しました」や断られた理由を、いまの会社のタブへ書いてしまう。
       * 切り替えていたら書かない（一覧の読み直しで、前の会社の行は新しくなる）。
       */
      const 対象 = this.selectedAccountTarget()
      this.saving = true
      this.contractResult = { type: 'success', text: '' }
      let updated = false
      try {
        await api.updateContract(this.contractForm)
        updated = true
        if (this.stillOnInvitationTarget(対象)) {
          this.contractResult = { type: 'success', text: '契約情報を更新しました。' }
          // プランが変わると最低月額のタブの額も変わる。同じ会社のままでも読み直させる。
          this.contractRevision += 1
        }
        /*
         * プランを替えると、プランマスターの「契約中」の社数も変わる。会社の一覧と
         * 一緒に読み直す（プランの表の読み直しは失敗しても何も投げない）。
         */
        await Promise.all([this.loadAccounts(), this.reloadPlanPricing()])
      } catch (error) {
        /*
         * **更新できたあとの一覧の読み直しで落ちたときは、「更新できません
         * でした」と言わない**（2026-09-26）。前は緑の「更新しました」と赤の
         * 「更新できませんでした」が上端に並んでいた。読み直しの失敗は
         * loadAccounts が上端（読み込みの失敗）へ書く。
         */
        if (!updated && this.stillOnInvitationTarget(対象)) {
          this.contractResult = {
            type: 'danger',
            text: this.failureText(error, '契約情報を更新できませんでした。時間をおいて再度お試しください。')
          }
        }
      } finally {
        this.saving = false
      }
    },
    /**
     * 利用可能機能を切り替える。
     *
     * 押した項目は一覧から来て、送り先は選んでいる会社。この2つがずれると
     * <b>見ている一覧と違う会社へ書き込む。</b>塞いだのは selectAccount の
     * 側で、切り替えた瞬間に一覧を空にした。押す対象が無くなるので、
     * ずれた組み合わせを作れない。
     *
     * ここで宛先を控えるのは、<b>返ってきたあとの照合のため</b>。
     * updateFeature を呼ぶ時点では控えた値と selectedAccount.id は必ず
     * 同じなので、<b>宛先の受け渡しそのものは何も守っていない</b>
     * （壊しても検査が落ちない。将来 await が前に入ったときのための形）。
     * 効いているのは、一覧を空にすることと、返ってからの stillOn の照合。
     */
    async changeFeature (item, enabled) {
      const 対象 = this.selectedAccountTarget()
      if (!対象) return
      if (!this.canEditFeatures) {
        this.featureRevision += 1
        return
      }
      /*
       * 結果はそのスイッチの下に出す（2026-09-26）。前は画面上端で、タブから
       * 遠くて見えなかった。前の結果は消しておく（残ると、いま押した結果に見える）。
       * $set を使わず作り直すのは、素の持ち物で動かす試験でも同じに動かすため。
       */
      this.featureResults = Object.assign({}, this.featureResults, {
        [item.featureCode]: { type: 'success', text: '' }
      })
      try {
        await api.updateFeature({
          accountId: 対象.accountId,
          featureCode: item.featureCode,
          enabled
        })
        const applied = await this.reloadFeatures(対象)
        if (!applied) return
        this.featureResults = Object.assign({}, this.featureResults, {
          [item.featureCode]: {
            type: 'success',
            text: `${item.featureName}を${enabled ? '利用可能' : '利用不可'}にしました。`
          }
        })
      } catch (error) {
        if (!this.stillOnInvitationTarget(対象)) return
        this.featureResults = Object.assign({}, this.featureResults, {
          [item.featureCode]: {
            type: 'danger',
            text: this.failureText(error, '利用可能機能を更新できませんでした。')
          }
        })
        /*
         * **断られたら、本当の値へ戻す。**読み直して、スイッチを作り直す。
         * 戻さないと、保存されていないのに印だけ切り替わって見える。
         */
        try {
          await this.reloadFeatures(対象)
        } catch (ignored) {
          // 読み直しに失敗しても、作り直しで押す前の値へは戻る
        }
        // 読み直しを待つ間に会社が変わっていたら、別の会社のスイッチを作り直さない
        if (this.stillOnInvitationTarget(対象)) this.featureRevision += 1
      }
    },
    /**
     * 利用可能機能の一覧を読み直す。**反映するのは、いちばん新しく始めた読み直しだけ。**
     * 古い応答が後から届いても、新しい状態を上書きしない。反映したら true。
     */
    async reloadFeatures (対象) {
      // 前の会社の処理は、番号を進める前に止める。進めると今の会社の読み直しが捨てられる（2026-09-22 監査 R1）
      if (!this.stillOnInvitationTarget(対象)) return false
      this.featureListSeq += 1
      const mine = this.featureListSeq
      const features = await api.getFeatures(対象.accountId)
      if (!this.stillOnInvitationTarget(対象) || mine !== this.featureListSeq) return false
      this.features = Array.isArray(features) ? features : []
      return true
    },
    /**
     * ［管理］の中のタブを切り替えたとき。**「操作履歴」を開いたら、履歴を読み直す。**
     *
     * <p>履歴を読むのは一覧の［管理］を押したときだけだった。同じ［管理］の中で
     * 保存してから「操作履歴」を押すと、開いたときの一覧のままで、いまの保存の行が
     * 出ない（2026-10-09、画面で確かめて見つけた）。保存した人が「記録されたか」を
     * 見に行く、いちばん普通の順で古い一覧が出ていた。</p>
     *
     * <p><b>読み直すのは、タブを開いたときの1か所。</b>保存の口ごとに読み直すと、
     * 履歴に行を残す保存を足すたびに、読み直しの足し忘れが起きる。</p>
     *
     * <p>どのタブを開いたかは、番号ではなく、<b>履歴の中身を入れた枠が表に出ているか</b>で
     * 見る。タブは人によって数が違う（一時パス・請求と入金は、出る人と出ない人がいる）。</p>
     */
    onManagementTabChanged () {
      this.$nextTick(() => {
        const body = this.$refs.auditLogTabBody
        if (body && body.closest && body.closest('.tab-pane.active')) {
          this.reloadAuditLogs()
        }
        /*
         * 「お支払い方法」を開いたら、中身を読み直す（番号を進めると、タブの中身が読み直す）。
         * 「利用可能機能」で許可を切り替えたあとや、お客様がカードを登録したあとに開いたとき、
         * ［管理］を押した時点の値を見せない。
         * 開き直すと、中身は表示の世代を進める。前に押した保存の応答が遅れて届いても、
         * 開き直したあとのチェック・結果の文・処理中の状態は変えない（AccountPaymentMethods の open・save）。
         */
        const payment = this.$refs.paymentMethodsTabBody
        if (payment && payment.closest && payment.closest('.tab-pane.active')) {
          this.paymentMethodsRevision += 1
        }
      })
    },
    /**
     * 操作履歴を読み直す。**反映するのは、いま開いている会社の、いちばん新しく始めた
     * 読み直しだけ。**
     *
     * <p>待つあいだに別の会社の［管理］を押したら、返ってきた履歴は捨てる（前の会社の
     * 履歴を、いまの会社の欄に出さない）。失敗も同じで、前の会社の失敗を、いまの会社の
     * 画面に出さない。失敗したときは、開いたときと同じ文を同じ場所に出し、出ている
     * 一覧はそのまま残す。反映したら true。</p>
     */
    async reloadAuditLogs () {
      const 対象 = this.selectedAccountTarget()
      if (!対象) return false
      this.auditLogListSeq += 1
      const mine = this.auditLogListSeq
      const まだ自分の番か = () =>
        this.stillOnInvitationTarget(対象) && mine === this.auditLogListSeq
      try {
        const auditLogs = await api.getAuditLogs(対象.accountId)
        if (!まだ自分の番か()) return false
        this.auditLogs = Array.isArray(auditLogs) ? auditLogs : []
        this.auditLogsReloadedSeq = 対象.seq
        return true
      } catch (error) {
        if (!まだ自分の番か()) return false
        this.handleError(error, '契約企業の詳細を取得できませんでした。画面を再読み込みしてください。')
        return false
      }
    },
    /**
     * 断られた理由の文言を作る。
     *
     * <p><b>出す先は呼ぶ側が決める。</b>幕を開いたままにする操作は
     * 画面上端へ書いても読まれない（{@link data} の createError を見よ）。
     * 文言の作り方だけをここに置き、<b>サーバの言い分をどこへ出しても
     * 同じ形になる</b>ようにする。</p>
     */
    failureText (error, fallback) {
      const data = error && error.response && error.response.data
      const status = error && error.response && error.response.status
      const detail = data && (data.message)
      // ステータス番号は画面へ出さず、調査用にコンソールへ残す。
      if (status) console.error(`契約管理の操作に失敗しました (${status})`, safeErrorDigest(error))
      /*
       * **サーバが理由を返したら、再試行の案内は付けない**（2026-09-22 監査 W1）。
       * 重複・受諾済み・入力の誤りのように、時間をおいても解決しない理由にまで
       * 「時間をおいて再度お試しください」が付いていた。案内は、理由が返らなかったとき
       * （通信の失敗など）だけ付ける。
       */
      if (!detail) return fallback
      const 前置き = String(fallback).replace(/(時間をおいて再度お試しください。|画面を再読み込みしてください。)+$/, '')
      return `${前置き} ${detail}`
    },
    handleError (error, fallback) {
      this.errorMessage = this.failureText(error, fallback)
    },

    /**
     * 招待の結果を帯へ出す。
     *
     * <p><b>通ったが届かなかった、を緑で出さない。</b>招待の作成・再送・
     * 宛先変更は、行は作れてもメール送信だけが落ちることがある。back は
     * そのとき送れたかどうかと理由を返しているのに、画面は文言を
     * 決め打ちして捨てていた。緑で「送信しました」と出ると運営はそこで
     * 手を離し、招待された人には何も届かない。</p>
     *
     * <p>鍵の名前が2つある。契約企業の作成は
     * {@code invitationEmailSent}、再送と宛先変更は {@code emailSent}。
     * <b>どちらか片方だけを見ると、見ていないほうが黙って緑になる。</b></p>
     *
     * <p>送れたかどうかが分からない（鍵がどちらも無い）ときは緑にする。
     * 送信が絡まない操作もここを通るため。</p>
     *
     * <p><b>上端の帯へ出すのは、上端の［契約企業を追加］の結果だけ</b>（2026-09-26）。
     * 再送・宛先変更は招待のカードの下へ出す（invitationResultOf）。</p>
     */
    announceInvitationResult (result, 既定の成功文) {
      const 送れなかった = emailWasNotSent(result)
      const 文 = (result && result.message) || 既定の成功文
      if (送れなかった) {
        this.warningMessage = 文
        this.successMessage = ''
      } else {
        this.successMessage = 文
        this.warningMessage = ''
      }
      /*
       * 前の操作の赤帯は、どちらの場合も消す（2026-09-23）。赤帯は会社を
       * 選び直すまで消えないので、残すと緑と赤が同時に出て、いま押した操作が
       * 成功したのか失敗したのか分からない。
       */
      this.errorMessage = ''
    },

    /** その機能のスイッチの下に出す結果。無ければ空（部品は何も出さない）。 */
    featureResultOf (featureCode) {
      return (this.featureResults || {})[featureCode] || { type: 'success', text: '' }
    }
  }
}
</script>

<style scoped>
.platform-access-page { min-width: 0; padding: 24px; }
/* 「利用可能機能」タブの、お支払い方法の許可の行き先の案内。 */
.feature-payment-moved { margin: 0 0 12px; color: #17384d; font-size: 13px; line-height: 1.6; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.page-header__copy { min-width: 0; }
.page-header h1 { margin: 0 0 8px; font-weight: 700; }
.page-header p { max-width: 820px; margin-bottom: 0; overflow-wrap: anywhere; }
.page-header__action, .primary-action, .mobile-primary-action { min-height: 42px; white-space: normal; }
/* 上端の［契約企業を追加］は折らず、縮めない（字を 12px にしたら見出しの横で「追／加」と割れた）。下の説明の文（p）は今までどおり折る。 */
button.page-header__action { flex: 0 0 auto; white-space: nowrap; }
.eyebrow { margin: 0 0 4px; color: #60738a; font-size: 12px; font-weight: 700; letter-spacing: .16em; }
.provider-scope-alert { line-height: 1.7; }
.access-card { min-width: 0; margin-bottom: 18px; }
.section-header, .selected-account-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; overflow-wrap: anywhere; }
.selected-account-header > div { display: grid; gap: 4px; }
.selected-account-header small { color: #657987; font-weight: 400; }
.responsive-table-shell { width: 100%; min-width: 0; overflow-x: auto; -webkit-overflow-scrolling: touch; }
/*
  表の最小の幅。画面の幅 1280px のとき表の入る幅は約 849px なので、900px だと右端の［管理］の欄が
  枠の外へ出ていた。契約企業の一覧は9列で、見出し（折り返さない・12px）だけで約 750px 使う。
  これは下限で、短い値を折らない（accountFields）ので、中身によっては表がこれより広くなる。
  そのときは横に送り、［管理］の欄は右端に留める（account-list-shell の action-cell）。
*/
.responsive-table-shell::v-deep table { min-width: 800px; }
/* スイッチの欄は折り返せるようにする。スイッチの下に切り替えた結果の文を出すため（2026-09-26）。 */
.action-cell { white-space: nowrap; }
/*
  契約企業一覧の［管理］の欄は、表を横に送っても右端に留める（短い値を折らないので、表は枠より
  広くなることがある。［管理］が枠の外へ出て、押すために横へ送らせない）。
*/
.account-list-shell::v-deep .action-cell { position: sticky; right: 0; z-index: 1; background: #fff; box-shadow: -1px 0 0 #d8e4ec; }
/* 会社名・契約企業・販売代理店の長い名前は、文節で折る（「SF3確かめ株式会／社」と最後の1字が落ちないように。Chrome・Edge で効く）。 */
.account-list-shell::v-deep td { word-break: auto-phrase; }
.period-phrase { display: inline-block; white-space: nowrap; }
/*
  表とカードは同じ中身で、画面幅に応じて片方だけを出す。

  切り替えは .mobile-only / .desktop-only が持っている。ところが同じ div に
  付けた一覧クラスにも display があり、後ろに書いた方が勝つため、広い画面
  でも表の下にカードが並んでいた。組み合わせで明示して、書き順に左右され
  ないようにする。
*/
.mobile-only { display: none; }
.mobile-only.mobile-record-list { display: none; }
.mobile-record-list { display: grid; gap: 12px; }
.mobile-record-card { min-width: 0; padding: 14px; border: 1px solid #d8e4ec; border-radius: 12px; background: #fff; box-shadow: 0 6px 18px rgba(28, 68, 91, .06); }
.mobile-record-card__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.mobile-record-card__header strong { min-width: 0; color: #17384d; font-size: 15px; line-height: 1.45; overflow-wrap: anywhere; }
.mobile-record-card__header .badge { flex: 0 0 auto; white-space: nowrap; }
.mobile-record-card__date { flex: 0 0 auto; color: #64748b; font-size: 12px; line-height: 1.4; text-align: right; white-space: normal; }
.mobile-detail-list, .invitation-detail-list { display: grid; gap: 8px; margin: 0 0 14px; }
.mobile-detail-list > div, .invitation-detail-list > div { display: grid; grid-template-columns: minmax(110px, 40%) minmax(0, 1fr); gap: 10px; min-width: 0; }
.mobile-detail-list dt, .invitation-detail-list dt { margin: 0; color: #657987; font-size: 12px; font-weight: 700; }
.mobile-detail-list dd, .invitation-detail-list dd { min-width: 0; margin: 0; color: #17384d; text-align: right; overflow-wrap: anywhere; }
.mobile-primary-action { width: 100%; }
.mobile-switch-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 10px; border-top: 1px solid #e6edf2; font-weight: 700; }
.mobile-empty-state { padding: 28px 14px; border: 1px dashed #cbd8e1; border-radius: 12px; color: #687d8a; text-align: center; }
.invitation-card { padding: 20px; border: 1px solid #d8e5ec; border-radius: 15px; background: #fff; box-shadow: 0 8px 24px rgba(26, 65, 86, .07); }
.invitation-card__header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; gap: 12px; }
.invitation-card__header p { margin: 0; color: #687d89; font-size: 12px; font-weight: 800; }
.invitation-card__header h3 { margin: 4px 0 0; color: #17394d; font-size: 20px; }
.invitation-message { margin: 14px 0; padding: 10px 12px; border-radius: 9px; background: #f5f8fa; color: #58707d; font-size: 12px; }
.invitation-actions { display: flex; flex-wrap: wrap; gap: 9px; }
.form-block-title { margin: 22px 0 12px; padding-bottom: 7px; border-bottom: 1px solid #e1e9ee; color: #2b4c5f; }
.free-plan-check { display: flex; align-items: center; gap: 8px; margin: 0 0 12px; font-weight: 600; color: #17384d; }
.form-note { margin: 8px 0 0; padding: 11px 13px; border-radius: 9px; background: #f3f7f9; color: #5b7380; font-size: 12px; line-height: 1.7; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; width: 100%; }
.platform-access-page::v-deep .access-tabs .nav-tabs { flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
.platform-access-page::v-deep .access-tabs .nav-link { white-space: nowrap; }

@media (max-width: 767.98px) {
  .platform-access-page { padding: 12px 0 92px; }
  .page-header { flex-direction: column; gap: 16px; margin-bottom: 16px; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .page-header__action, .primary-action, .section-header__action { width: 100%; min-height: 44px; }
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .mobile-only.mobile-record-list { display: grid; }
  .section-header { align-items: stretch; flex-direction: column; }
  .platform-access-page::v-deep .card-header { padding: 14px; }
  .platform-access-page::v-deep .card-body { padding: 12px; }
  .selected-account-header strong { font-size: 15px; line-height: 1.5; }
  .contract-form-row { margin-right: -6px; margin-left: -6px; }
  .contract-form-row > div { padding-right: 6px; padding-left: 6px; }
  .mobile-record-card__header, .invitation-card__header { flex-wrap: wrap; }
  .mobile-record-card__date { flex-basis: 100%; text-align: left; }
  .mobile-detail-list > div, .invitation-detail-list > div { grid-template-columns: minmax(0, 1fr); gap: 3px; padding: 8px 0; border-bottom: 1px solid #e6edf2; }
  .mobile-detail-list > div:last-child, .invitation-detail-list > div:last-child { border-bottom: 0; }
  .mobile-detail-list dd, .invitation-detail-list dd { text-align: left; }
  .invitation-actions { display: grid; grid-template-columns: minmax(0, 1fr); }
  .invitation-actions .btn { width: 100%; min-height: 44px; margin: 0; white-space: normal; }
  .modal-actions { flex-direction: column-reverse; gap: 8px; }
  .modal-actions .btn { width: 100%; min-height: 44px; margin: 0; white-space: normal; }
  .platform-access-page::v-deep .access-modal .modal-dialog { width: auto; max-width: calc(100vw - 16px); margin: 8px; }
  .platform-access-page::v-deep .access-modal .modal-body { max-height: calc(100vh - 190px); overflow-y: auto; }
  .platform-access-page::v-deep .access-modal .modal-footer { padding: 12px; }
}

@media (min-width: 768px) {
  .desktop-only { display: block; }
}
</style>
